import { closestCenter, DndContext, type DragEndEvent, type DragOverEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { horizontalListSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Box, ButtonBase, CircularProgress, Collapse, IconButton, Skeleton, type SxProps, type Theme, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import {
	DataGrid,
	type DataGridProps,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	type GridCellParams,
	type GridColDef,
	type GridColumnGroupingModel,
	type GridColumnVisibilityModel,
	GridFooterContainer,
	GridPagination,
	type GridPaginationModel,
	type GridRowId,
	type GridRowParams,
	type GridValidRowModel,
	gridColumnVisibilityModelSelector,
} from "@mui/x-data-grid";
import { type KeyboardEvent, memo, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLatestRef } from "../../../../hooks/useLatestRef";
import { useMultiSort } from "../../../../hooks/useMultiSort";
import { DISABLED_CONTROL, FONT_SIZE, INK, MOTION, NEUTRAL } from "../../../../theme/tokens";
import { findMatchingSortOption, type MultiSortOption, type SortingState } from "../../../../utils/multiColumnSorting";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { SortableDataGridHeader } from "../../Clickable/SortableDataGridHeader";
import { SystemSearchField } from "../../Inputs/SystemSearchField";
import SystemDialog from "../../Popups/Dialog";
import { SystemSortingButtonWithPopup } from "../../Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import { ColumnPickerPopover } from "./ColumnPickerPopover";
import { placeNewlyVisibleInBands, slotReorderedFields } from "./columnOrderUtils";
import { EXPORT_FORMATS, type ExportFormat, exportRowsAsCsv, exportRowsAsXlsx } from "./exportGridData";
import { GridExportButton } from "./GridExportButton";
import { ReorderableColumnHeader } from "./ReorderableColumnHeader";
import { RowExpansionStateContext, useRowExpansionState } from "./rowExpansionState";
import { sortRowsWithPins } from "./rowSortPins";
import { buildSystemGridColumnMenu } from "./SystemGridColumnMenu";
import {
	isCellExpansion,
	isControlledRowExpansion,
	isControlledRowGroupCollapse,
	isRowExpansion,
	type RowExpandIndicator,
	type RowGroup,
	type RowGroupToggleTarget,
	type SystemDataGridProps,
} from "./types";
import { useColumnAnchoredPanel } from "./useColumnAnchoredPanel";
import { useColumnOrder } from "./useColumnOrder";
import { useColumnVisibility } from "./useColumnVisibility";
import { useColumnWidths } from "./useColumnWidths";
import {
	appendHeaderClass,
	cellFocusOffSx,
	DETAIL_ROW_CLASS,
	EXPAND_COLUMN_RENDERED_WIDTH_PX,
	EXPAND_FIELD_WIDTH,
	extractSearchValue,
	GROUP_BAND_PADDING_X_PX,
	gridBaseProps,
	HIDE_HEADER_SEPARATOR_CLASS,
	hoverCheckboxSx,
	MUI_CELL_PADDING_X_PX,
	mergeSx,
	SORTABLE_HEADER_CLASS,
	useSkeletonWidths,
} from "./utils";

// Type the custom no-rows overlay slot prop (MUI's extension point — avoids a cast).
declare module "@mui/x-data-grid" {
	interface NoRowsOverlayPropsOverrides {
		// `emptyContent`, not `content` — `content` collides with the overlay's DOM attribute.
		emptyContent?: ReactNode;
	}
}

const SKELETON_ROW_COUNT = 3;
type SkeletonRow = { __skeletonId: number };

/** Default min height (px) for `containedHeight` — keeps a sparse grid from looking cramped. */
export const DEFAULT_CONTAINED_MIN_HEIGHT = 180;
/** Default max height (px) for `containedHeight` — past this the grid scrolls internally. */
export const DEFAULT_CONTAINED_MAX_HEIGHT = 560;

/** Default search-field width (px); callers can narrow it when the toolbar row is crowded. */
const DEFAULT_SEARCH_WIDTH_PX = 280;

/** A fixed width, or a range the field flexes within (grow into spare toolbar room, shrink before wrapping). */
/** Min height (px) for the no-rows overlay so a centered EmptyState isn't clipped. */
const EMPTY_OVERLAY_MIN_HEIGHT = 240;
/** Header + footer height added to the overlay room when flooring an empty grid. */
const EMPTY_GRID_CHROME_ALLOWANCE = 96;

// The toolbar popups (sort + column picker) portal to <body> via react-tiny-popover, which sets no
// z-index by default. Give their container a z-index above MUI's modal (1300) so they aren't hidden behind
// a Dialog when the grid renders inside one (e.g. the Add/Remove nurse dialog). Consumers can raise it
// further via `columnPicker.zIndex` for deeper stacking contexts (nested modals).
const TOOLBAR_POPUP_Z_INDEX = "1301";

// Grouped header band: each group's band cell gets a stable class so the caller-supplied per-group
// background can be applied via sx (dynamic keys can't be styled inline on MUI's group header cell).
// The key is sanitized to valid CSS class characters.
const HEADER_GROUP_CLASS_PREFIX = "system-datagrid-header-group-";
const headerGroupClass = (key: string): string => `${HEADER_GROUP_CLASS_PREFIX}${key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

const EXPAND_FIELD = "__expandToggle";
/** `fontSize="small"` glyph box. Whatever replaces the chevron must fill it, or the button resizes mid-click. */
const EXPAND_GLYPH_BOX_PX = 20;
const EXPAND_SPINNER_SIZE_PX = 16;
/** An opening detail's height before it is measured: its Collapse starts shut. */
const COLLAPSED_DETAIL_HEIGHT_PX = 0;
/** `CircularProgress`'s `size` sizes its root, so the ring needs margin to occupy the glyph's box. */
const EXPAND_SPINNER_MARGIN_PX = (EXPAND_GLYPH_BOX_PX - EXPAND_SPINNER_SIZE_PX) / 2;
/** Heavier than MUI's 3.6 default, which nearly disappears at 16px. */
const EXPAND_SPINNER_THICKNESS = 4;
const DEFAULT_PAGE_SIZE = gridBaseProps.initialState?.pagination?.paginationModel?.pageSize ?? 50;
const DETAIL_ID_PREFIX = "__detail__";
const DETAIL_ANIMATION_MS = 200;
// `lineHeight: normal` is load-bearing, not cosmetic. MUI sets `line-height: <rowHeight>px` on every
// cell to vertically centre plain text, and the detail area is a cell — so that value was inherited by
// the whole panel. Any `display: block` wrapper in there then got a line-box strut the height of the
// ROW: a 40px time picker sat inside a 107px box, stacking ~90px of phantom space per control and
// pushing the panel past the height its host had reserved for it.
const DETAIL_CELL_DEFAULT_SX = { width: "100%", py: 1, px: 2, lineHeight: "normal" } as const;
const detailRowCellResetSx = { [`& .${DETAIL_ROW_CLASS} .MuiDataGrid-cell`]: { p: 0 } } as const;
const CELL_EXPANDABLE_CLASS = "system-datagrid-expandable-cell";
const CELL_EXPANDABLE_FOCUS_RING_PX = 2;
const cellExpandableCursorSx = (theme: Theme) => ({
	[`& .${CELL_EXPANDABLE_CLASS}`]: { cursor: "pointer" },
	[`& .${CELL_EXPANDABLE_CLASS}:hover`]: { backgroundColor: "action.hover", transition: "background-color 150ms" },
	// Keyboard parity for the hover rule above. MUI focuses the CELL element, so a cell's own content
	// cannot light itself up on focus — the cue has to be painted from here. `.MuiDataGrid-cell` is in
	// the selector to outrank `cellFocusOffSx`, which is merged after this and blanks every cell outline.
	[`& .${CELL_EXPANDABLE_CLASS}.MuiDataGrid-cell:focus-visible`]: {
		backgroundColor: "action.hover",
		outline: `${CELL_EXPANDABLE_FOCUS_RING_PX}px solid ${theme.palette.primary.main}`,
		outlineOffset: -CELL_EXPANDABLE_FOCUS_RING_PX,
	},
});
// Column-reorder drop indicator: classes toggled imperatively on the hovered header CELL during a
// drag (cheaper than re-rendering the grid) and painted as a full-height inset bar flush on the
// column boundary — left edge for "drops before", right edge for "drops after".
const COLUMN_DROP_LEFT_CLASS = "system-datagrid-drop-left";
const COLUMN_DROP_RIGHT_CLASS = "system-datagrid-drop-right";
const COLUMN_DROP_BAR_WIDTH_PX = 2;
const columnReorderMarkerSx = (theme: Theme) => ({
	[`& .MuiDataGrid-columnHeader.${COLUMN_DROP_LEFT_CLASS}`]: { boxShadow: `inset ${COLUMN_DROP_BAR_WIDTH_PX}px 0 0 0 ${theme.palette.primary.main}` },
	[`& .MuiDataGrid-columnHeader.${COLUMN_DROP_RIGHT_CLASS}`]: { boxShadow: `inset -${COLUMN_DROP_BAR_WIDTH_PX}px 0 0 0 ${theme.palette.primary.main}` },
});

// ── Row grouping (opt-in `rowGrouping`) ───────────────────────────────────────────────────────
// Group bands are synthetic rows interleaved into the row set, exactly like detail rows: the band
// occupies a real grid row whose FIRST cell col-spans the full width. Reusing that mechanism (rather
// than a second header layer) is what lets grouping compose with expansion, sort and search untouched.
const GROUP_ID_PREFIX = "__group__";
const GROUP_ROW_CLASS = "system-datagrid-group-row";
/** Modifier on a `persistentGroupKeys` band with no rows under it: nothing to fold, nothing to hover. */
const GROUP_ROW_EMPTY_CLASS = "system-datagrid-group-row--empty";
const DEFAULT_GROUP_HEADER_HEIGHT = 40;
const GROUP_TOGGLE_ANIMATION_MS = MOTION.DURATION.CONTROL;
// The band is a structural divider, not data: a faint neutral wash plus a hairline above it, with the
// cell's own padding and focus ring dropped so the ButtonBase inside fills the strip edge to edge.
const groupRowChevronSx = {
	[`& .${GROUP_ROW_CLASS} .MuiDataGrid-cell`]: {
		p: 0,
		backgroundColor: NEUTRAL[50],
		borderTop: `1px solid ${NEUTRAL[200]}`,
		outline: "none",
	},
} as const;
// `toggleTarget: "band"` adds a hover wash, since the whole strip is the button. `"chevron"` keeps the
// chrome above without it: the strip is no longer the target, so a wash would promise a click the
// label doesn't answer.
const groupRowSx = {
	...groupRowChevronSx,
	[`& .${GROUP_ROW_CLASS}:hover .MuiDataGrid-cell`]: { backgroundColor: NEUTRAL[100] },
	// An empty band is a statement ("no one here"), not a control: it keeps the resting wash under the
	// pointer, so it never promises an action it doesn't have. Same specificity as the rule above and
	// declared after it, so it wins.
	[`& .${GROUP_ROW_CLASS}.${GROUP_ROW_EMPTY_CLASS}:hover .MuiDataGrid-cell`]: { backgroundColor: NEUTRAL[50] },
	[`& .${GROUP_ROW_CLASS}.${GROUP_ROW_EMPTY_CLASS}`]: { cursor: "default" },
} as const;
/** The chevron's rotation: transform-only, so the fold reads as one motion rather than an icon swap. */
const groupChevronSx = (collapsed: boolean) =>
	({
		color: "text.secondary",
		transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
		transition: `transform ${GROUP_TOGGLE_ANIMATION_MS}ms ${MOTION.EASE_OUT}`,
		"@media (prefers-reduced-motion: reduce)": { transition: "none" },
	}) as const;

/**
 * A band row. It carries its own group, so the band cell reads the group from the row it renders rather than
 * from a lookup: band rows are rebuilt whenever the groups change (`groupedRows`), so the row is always current.
 * `__groupEmpty` marks a persistent group's band that has no rows under it (see `persistentGroupKeys`).
 */
type GroupHeaderRow<T extends GridValidRowModel = GridValidRowModel> = { __groupKey: string; __group: RowGroup<T>; __groupEmpty?: true };
function isGroupRow<T extends GridValidRowModel = GridValidRowModel>(row: GridValidRowModel): row is GroupHeaderRow<T> {
	return "__groupKey" in row;
}

type DetailRow<T> = { __detailParentId: string | number; __detailParent: T };
type GridRow<T extends GridValidRowModel> = T | DetailRow<T> | GroupHeaderRow<T>;
function isDetailRow<T>(row: GridValidRowModel): row is DetailRow<T> {
	return "__detailParentId" in row;
}
/** Neither a band nor a detail row — i.e. one of the caller's own rows. */
function isDataRow<T extends GridValidRowModel>(row: GridRow<T>): row is T {
	return !isDetailRow<T>(row) && !isGroupRow(row);
}

interface ExpandToggleCellProps {
	/** Whether it is open, or (lazy rows) has a resolve in flight, is read from `RowExpansionStateContext`. */
	rowId: string | number;
	onToggle: (id: string | number) => void;
	expandLabel: string;
	collapseLabel: string;
	loadingLabel: string;
	/** The closed chevron's tooltip, set only when it needs explaining (a lazy row's "load and expand"). */
	closedTooltip?: string;
	/**
	 * Keep the tooltip wrapper mounted even in the states that have no tooltip — required for any grid
	 * whose chevron tooltip can come and go, because dropping the wrapper changes this component's ROOT
	 * ELEMENT TYPE and React then remounts the button (see the render below).
	 */
	keepTooltipMounted: boolean;
	/** `"rotate"` turns one chevron (the band's glyph and motion); `"swap"` swaps a down arrow for an up arrow. */
	indicator: RowExpandIndicator;
	testId?: string;
	loadingTestId?: string;
}

const ExpandToggleCell = memo(function ExpandToggleCell({
	rowId,
	onToggle,
	expandLabel,
	collapseLabel,
	loadingLabel,
	closedTooltip,
	keepTooltipMounted,
	indicator,
	testId,
	loadingTestId,
}: ExpandToggleCellProps) {
	const state = useRowExpansionState();
	const isOpen = state.expanded.has(rowId);
	// Lazy rows only: a resolve is in flight — the spinner takes the chevron's place, clicks are ignored.
	const isPending = state.pending.has(rowId);
	const tooltip = isPending ? loadingLabel : isOpen ? undefined : closedTooltip;
	const toggle = (
		<IconButton
			size="small"
			aria-label={isPending ? loadingLabel : isOpen ? collapseLabel : expandLabel}
			// A disclosure: whether the row's detail is open, as the band chevron says it for its band.
			aria-expanded={isOpen}
			// Stays enabled while pending: disabling would drop focus and swallow the tooltip.
			aria-busy={isPending || undefined}
			onClick={(e) => {
				// Stop propagation so a caller's onRowClick (e.g. row navigation) doesn't fire on toggle.
				e.stopPropagation();
				onToggle(rowId);
			}}
			data-testid={testId}
		>
			{isPending ? (
				// `aria-hidden` — the button already publishes `loadingLabel` and `aria-busy`; naming the
				// nested progressbar too would announce the same string twice.
				<CircularProgress
					size={EXPAND_SPINNER_SIZE_PX}
					thickness={EXPAND_SPINNER_THICKNESS}
					aria-hidden
					sx={{ m: `${EXPAND_SPINNER_MARGIN_PX}px` }}
					data-testid={loadingTestId}
				/>
			) : indicator === "rotate" ? (
				<KeyboardArrowDownIcon fontSize="small" sx={groupChevronSx(!isOpen)} />
			) : isOpen ? (
				<KeyboardArrowUpIcon fontSize="small" />
			) : (
				<KeyboardArrowDownIcon fontSize="small" />
			)}
		</IconButton>
	);
	// The wrapping element must not change between renders. React reconciles by type, so letting the
	// tooltip come and go would remount the button and destroy focus — on a keyboard user's Enter, right
	// as the row loads. Grids whose tooltip can vary keep the wrapper mounted and vary only `title`; an
	// empty title is inert, and the accessible name stays the button's own.
	if (keepTooltipMounted) return <InHouseTooltip title={tooltip ?? ""}>{toggle}</InHouseTooltip>;
	return tooltip ? <InHouseTooltip title={tooltip}>{toggle}</InHouseTooltip> : toggle;
});

/** The expand column's header: "Collapse all" while any row is open, else "Expand all" on grids that opt in. */
const ExpandAllToggleHeaderCell = memo(function ExpandAllToggleHeaderCell({
	onClick,
	label,
	icon,
	testId,
}: {
	onClick: () => void;
	label: string;
	icon: ReactNode;
	testId?: string;
}) {
	return (
		<InHouseTooltip title={label}>
			<IconButton size="small" aria-label={label} onClick={onClick} data-testid={testId}>
				{icon}
			</IconButton>
		</InHouseTooltip>
	);
});

/**
 * `isolateExpandedContentKeys`: stops a key typed inside the detail content from bubbling to the host cell,
 * whose `onKeyDown` feeds the grid's cell navigation. Escape is let through for a page-level handler.
 */
const stopDetailKeyPropagation = (event: KeyboardEvent<HTMLDivElement>) => {
	if (event.key !== "Escape") event.stopPropagation();
};

const RowDetailCell = memo(function RowDetailCell({
	parentId,
	isolateKeys,
	children,
	testId,
	sx,
}: {
	/** The row it details: whether that row is open, and whether it is animating, come from `RowExpansionStateContext`. */
	parentId: string | number;
	isolateKeys: boolean;
	children: ReactNode;
	testId?: string;
	sx?: SxProps<Theme>;
}) {
	const state = useRowExpansionState();
	const open = state.expanded.has(parentId);
	const animate = state.transitioning.has(parentId);
	return (
		<Collapse in={open} appear={animate} timeout={DETAIL_ANIMATION_MS} sx={{ width: "100%" }}>
			<Box sx={mergeSx(DETAIL_CELL_DEFAULT_SX, sx)} data-testid={testId} onKeyDown={isolateKeys ? stopDetailKeyPropagation : undefined}>
				{children}
			</Box>
		</Collapse>
	);
});

const GROUP_BAND_ROW_SX = {
	display: "flex",
	alignItems: "center",
	gap: 1,
	width: "100%",
	height: "100%",
	px: `${GROUP_BAND_PADDING_X_PX}px`,
	minWidth: 0,
} as const;
/**
 * Grouping composed with ROW expansion: the band's chevron sits in a slot as wide as the toggle column below it,
 * so the band chevron and every row chevron share one centre line, and the label starts where the first data
 * column's text does.
 */
const GROUP_BAND_ROW_ALIGNED_SX = { ...GROUP_BAND_ROW_SX, gap: 0, pl: 0 } as const;
const GROUP_BAND_CHEVRON_SLOT_SX = {
	width: EXPAND_COLUMN_RENDERED_WIDTH_PX,
	flexShrink: 0,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;
const GROUP_BAND_LABEL_ALIGNED_INSET_SX = { pl: `${MUI_CELL_PADDING_X_PX}px` } as const;

/**
 * One group band: the chevron plus the caller's label node, filling the row's col-spanned first cell.
 *
 * `toggleTarget: "band"` — collapsible bands are a single `ButtonBase` over the whole strip rather than
 * a bare icon button: the band reads as one target, and `aria-expanded` on the element that *is* the
 * whole row is what tells assistive tech what the chevron actually controls.
 *
 * `toggleTarget: "chevron"` — only the chevron is a button (it carries `aria-expanded` and the name),
 * and the label is a plain `Box` filling the rest of the strip, so the caller's label can hold its own
 * buttons without nesting one inside another.
 */
const GroupHeaderCell = memo(function GroupHeaderCell({
	collapsed,
	collapsible,
	empty,
	toggleTarget,
	label,
	ariaLabel,
	onToggle,
	testId,
	tabIndex,
	alignToExpandColumn,
}: {
	collapsed: boolean;
	collapsible: boolean;
	/** A persistent group with no rows: the label dims as a whole and nothing folds (the caller passes `collapsible: false`). */
	empty: boolean;
	toggleTarget: RowGroupToggleTarget;
	label: ReactNode;
	ariaLabel: string;
	onToggle: () => void;
	testId?: string;
	/** The band cell's roving tab stop; applied to the chevron in `"chevron"` mode, so Tab leaves the grid. */
	tabIndex: -1 | 0;
	/** Row expansion is on: line the chevron up over the toggle column (see `GROUP_BAND_ROW_ALIGNED_SX`). */
	alignToExpandColumn: boolean;
}) {
	const rowSx = alignToExpandColumn ? GROUP_BAND_ROW_ALIGNED_SX : GROUP_BAND_ROW_SX;
	const labelInsetSx = alignToExpandColumn ? GROUP_BAND_LABEL_ALIGNED_INSET_SX : undefined;
	// The slot is kept even with no chevron (a search withholds it), so the label doesn't jump sideways.
	const place = (chevron: ReactNode): ReactNode =>
		alignToExpandColumn ? <Box sx={GROUP_BAND_CHEVRON_SLOT_SX}>{collapsible && chevron}</Box> : collapsible && chevron;
	if (toggleTarget === "chevron") {
		return (
			<Box sx={rowSx} data-testid={testId} data-empty={empty || undefined}>
				{place(
					<IconButton
						size="small"
						onClick={onToggle}
						aria-expanded={!collapsed}
						aria-label={ariaLabel}
						tabIndex={tabIndex}
						data-testid={testId ? `${testId}-toggle` : undefined}
					>
						<KeyboardArrowDownIcon fontSize="small" sx={groupChevronSx(collapsed)} />
					</IconButton>,
				)}
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flex: 1,
						minWidth: 0,
						...labelInsetSx,
						...(empty && { opacity: DISABLED_CONTROL.DIMMED_OPACITY }),
					}}
				>
					{label}
				</Box>
			</Box>
		);
	}
	const content = (
		<>
			{place(<KeyboardArrowDownIcon fontSize="small" sx={groupChevronSx(collapsed)} />)}
			{/* Dimmed as one unit rather than recoloured: the caller's label keeps its own ink and icons, and
			    the whole band recedes the way an unavailable row does. */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, ...labelInsetSx, ...(empty && { opacity: DISABLED_CONTROL.DIMMED_OPACITY }) }}>
				{label}
			</Box>
		</>
	);
	if (!collapsible) {
		return (
			<Box sx={rowSx} data-testid={testId} data-empty={empty || undefined}>
				{content}
			</Box>
		);
	}
	return (
		<ButtonBase
			onClick={onToggle}
			aria-expanded={!collapsed}
			aria-label={ariaLabel}
			data-testid={testId}
			sx={{ ...rowSx, justifyContent: "flex-start", textAlign: "start", borderRadius: 0 }}
		>
			{content}
		</ButtonBase>
	);
});

/** Centers the caller's `emptyState` node (from slotProps). Module-level + memoized for a stable identity. */
const NoRowsOverlay = memo(function NoRowsOverlay({ emptyContent }: { emptyContent?: ReactNode }) {
	// minHeight floors the room so the EmptyState isn't clipped; overflow visible keeps it from being cut.
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				height: "100%",
				minHeight: EMPTY_OVERLAY_MIN_HEIGHT,
				width: "100%",
				overflow: "visible",
			}}
		>
			{emptyContent}
		</Box>
	);
});

/**
 * Generic, sort-integrated DataGrid.
 *
 * - Owns search (declare `searchableColumns`).
 * - Owns sort (declare `sortingOptions` — column headers become clickable sort triggers,
 *   the sort popup is rendered next to the search input, and clicks on either mutate the
 *   same state).
 * - Owns async UX (`loading` shows 3 skeleton rows, `error` shows a translated message).
 * - Optionally owns column reorder (`enableColumnReorder`, off by default — drag-handle headers,
 *   per-user persisted order, toolbar reset). See `./useColumnOrder`.
 * - Toolbar suppresses itself when nothing is configured.
 *
 * See `./types.ts` for the full prop API.
 */
export function SystemDataGrid<T extends GridValidRowModel>(props: SystemDataGridProps<T>) {
	const {
		rows,
		columns,
		getRowId,
		searchableColumns,
		searchPlaceholder,
		searchValue: controlledSearchValue,
		onSearchChange,
		autoExpandSingleSearchResult = false,
		sortingOptions,
		defaultSortState,
		onSortChange,
		localStorageKey,
		dropUnofferedSorts = false,
		keepSortOnHeaderToggle = false,
		// DEFAULT: stack — the historical behavior, so the grids that ask for nothing keep the sort
		// gesture they shipped with. `"replace"` is opt-in per grid; see the prop docs.
		headerClickMode = "stack",
		showSortSummary = false,
		getRowSortPin,
		// DEFAULT: false — the toolbar sort button every sorting grid shipped with.
		hideSortButton = false,
		canFilterByColumn,
		onFilterByColumn,
		filterByHint,
		flushToolbar = false,
		skeletonRowCount = SKELETON_ROW_COUNT,
		hoverCheckboxSelection = false,
		enableColumnReorder = false,
		// DEFAULT: "when-customized" — every grid that reorders today shows reset only after a reorder; "always" is opt-in.
		columnResetVisibility = "when-customized",
		// DEFAULT: off — every existing grid keeps wrapping its toolbar controls one by one, as today.
		keepToolbarControlsTogether = false,
		// DEFAULT: off — the Sort trigger keeps its label everywhere it shows one today.
		sortOnlyShowIcon = false,
		onColumnOrderChange,
		persistColumnWidths = false,
		columnPicker,
		rowGrouping,
		headerGroups,
		processRowUpdate,
		onProcessRowUpdateError,
		isCellEditable,
		getRowExpandContent,
		getExpandContentHeight,
		isRowExpandable,
		onRowExpandRequest,
		getRowExpandTooltip,
		getRowExpandAriaLabel,
		// DEFAULT: false — every expansion grid shipped with the Collapse-all header.
		hideCollapseAllRowsHeader = false,
		// DEFAULT: "swap" — the up/down arrow swap every row-expansion grid shipped with.
		rowExpandIndicator = "swap",
		// DEFAULT: false — keys bubble to the grid's cell navigation, as they always have.
		isolateExpandedContentKeys = false,
		// DEFAULT: false — a toggle hands MUI fresh rows, the path every expansion grid shipped with.
		stableRowsOnToggle = false,
		getCellExpandContent,
		getCellExpandContentHeight,
		expandedContentSx,
		onExpandedRowsChange,
		// DEFAULT: off — only grids that ask for it get a header "Expand all", so every existing grid's header is unchanged.
		showExpandAll = false,
		loading,
		error,
		errorMessage,
		leadingToolbarNodes,
		trailingToolbarNodes,
		toolbarFooter,
		searchWidth = DEFAULT_SEARCH_WIDTH_PX,
		exportOptions,
		emptyState,
		sx,
		showBorderOnCellFocus = false,
		defaultPageSize,
		containedHeight,
		dataGridProps,
		apiRef: externalApiRef,
		dataTestId,
		toolbarSxOverride,
	} = props;

	// DEFAULT: "band" — the whole strip is the toggle, which is how every grid grouped before the option.
	const groupToggleTarget: RowGroupToggleTarget = rowGrouping?.toggleTarget ?? "band";

	// Contained-height: `height: auto` (overrides the base 100%) bounded by min/max, so the grid
	// sizes to its rows then scrolls. Resolved here so the wrappers below can drop their fill too.
	const containedHeightSx = useMemo<DataGridProps<T>["sx"] | null>(() => {
		if (!containedHeight) return null;
		const minHeight = (typeof containedHeight === "object" ? containedHeight.minHeight : undefined) ?? DEFAULT_CONTAINED_MIN_HEIGHT;
		const maxHeight = (typeof containedHeight === "object" ? containedHeight.maxHeight : undefined) ?? DEFAULT_CONTAINED_MAX_HEIGHT;
		return { height: "auto", minHeight, maxHeight };
	}, [containedHeight]);

	// Grouped header band: per-group band-cell backgrounds, keyed by the group's stable class. Merged into
	// the grid sx below (after the caller's sx) so it wins over the base header tint for tinted groups;
	// ungrouped/untinted band cells keep the default tint. Null when no colors were supplied.
	const headerGroupColorSx = useMemo<DataGridProps<T>["sx"] | null>(() => {
		const colors = headerGroups?.colors;
		if (!colors) return null;
		const entries: Record<string, { backgroundColor: string }> = {};
		for (const [key, color] of Object.entries(colors)) {
			entries[`& .MuiDataGrid-columnHeader.${headerGroupClass(key)}`] = { backgroundColor: color };
		}
		return entries;
	}, [headerGroups]);

	const effectiveSx = useMemo(() => {
		const withFocus = showBorderOnCellFocus ? sx : mergeSx(cellFocusOffSx, sx);
		// Contained sizing layers over the base `height: 100%`; the header-group tint layers last so it
		// overrides the base per-cell header background for grouped bands.
		const withContained = containedHeightSx ? mergeSx(withFocus, containedHeightSx) : withFocus;
		const withHeaderGroups = headerGroupColorSx ? mergeSx(withContained, headerGroupColorSx) : withContained;
		const withHoverCheckbox = hoverCheckboxSelection ? mergeSx(withHeaderGroups, hoverCheckboxSx) : withHeaderGroups;
		// Band chrome is inert without band rows, so it's keyed off the prop rather than the resolved flag.
		if (!rowGrouping) return withHoverCheckbox;
		return mergeSx(withHoverCheckbox, groupToggleTarget === "chevron" ? groupRowChevronSx : groupRowSx);
	}, [showBorderOnCellFocus, sx, containedHeightSx, headerGroupColorSx, hoverCheckboxSelection, rowGrouping, groupToggleTarget]);
	const { t } = useTranslation();

	// `!loading && !error` = the live grid (the one bound to apiRef) is mounted, not the skeleton/error
	// branch. The hook re-subscribes its menuOpen handler once that flips true, so panel anchoring works
	// even when the grid first renders in a loading state.
	const { apiRef, slotProps: anchoredPanelSlotProps } = useColumnAnchoredPanel(!loading && !error, externalApiRef);

	// Sort — only attach state when caller provided sortingOptions.
	const sortHook = useMultiSort<T>(localStorageKey ?? "", defaultSortState ?? [], sortingOptions, { dropUnofferedSorts });
	const sortEnabled = !!sortingOptions;

	// Mirror sort changes out to a caller (the sort analogue of `onColumnOrderChange`). Ref-held so a
	// changing callback identity never re-fires the effect; fires on mount + every sort-state change.
	const onSortChangeRef = useRef(onSortChange);
	onSortChangeRef.current = onSortChange;
	useEffect(() => {
		onSortChangeRef.current?.(sortHook.sortState);
	}, [sortHook.sortState]);

	// Per-field lookup of the matching MultiSortOption — keyed by the option's primary sortColumn.
	// Lets the auto-injected header use the option's full multi-key sortColumns (so a click and
	// a popup pick produce the same sort entry) and forward the customSortFunction if any.
	const optionByField = useMemo(() => {
		const map = new Map<keyof T, MultiSortOption<T>>();
		if (!sortingOptions) return map;
		for (const option of sortingOptions) {
			const primary = option.sortColumns?.[0];
			if (primary != null && !map.has(primary)) map.set(primary, option);
		}
		return map;
	}, [sortingOptions]);

	// Column reorder — opt-in. No-op passthrough when disabled (`orderedColumns === columns`).
	const {
		orderedColumns,
		fieldIds,
		isCustomized: columnOrderCustomized,
		moveField,
		applyOrder,
		resetOrder,
	} = useColumnOrder<T>({
		enabled: enableColumnReorder,
		localStorageKey,
		columns,
		onOrderChange: onColumnOrderChange,
	});

	// Grouped header band (opt-in): one group node per distinct `headerGroup`, its children the fields
	// carrying that key. Built from `orderedColumns` so it tracks reorder; MUI merges/splits the visual
	// bands by DISPLAY order at render, so the node order here is immaterial. Columns without a
	// `headerGroup` are simply omitted → they render under a blank band cell. `undefined` (feature off)
	// when no columns are grouped, so a grid that doesn't use the band pays nothing.
	const columnGroupingModel = useMemo<GridColumnGroupingModel | undefined>(() => {
		if (!headerGroups) return undefined;
		const { labels } = headerGroups;
		const order: string[] = [];
		const fieldsByGroup = new Map<string, string[]>();
		for (const col of orderedColumns) {
			const key = col.headerGroup;
			if (!key) continue;
			const field = String(col.field);
			const existing = fieldsByGroup.get(key);
			if (existing) existing.push(field);
			else {
				fieldsByGroup.set(key, [field]);
				order.push(key);
			}
		}
		if (order.length === 0) return undefined;
		return order.map((key) => ({
			groupId: key,
			// Fall back to the raw key when a label is missing — mirrors MUI's own `headerName ?? groupId`.
			headerName: labels[key] ?? key,
			headerAlign: "center" as const,
			headerClassName: headerGroupClass(key),
			children: (fieldsByGroup.get(key) as string[]).map((field) => ({ field })),
		}));
	}, [headerGroups, orderedColumns]);

	// Columns opt out of reorder via the native `disableReorder` flag: they get no drag handle and are
	// excluded from the sortable set, so they stay pinned and can't be a drop target for other columns.
	const nonReorderableFields = useMemo(() => new Set(orderedColumns.filter((c) => c.disableReorder).map((c) => String(c.field))), [orderedColumns]);
	const reorderableFieldIds = useMemo(() => fieldIds.filter((f) => !nonReorderableFields.has(f)), [fieldIds, nonReorderableFields]);

	// A drag in the column picker, which lists only some of the columns (see `slotReorderedFields`).
	const handlePickerReorder = useCallback((dragged: string[]) => applyOrder(slotReorderedFields(fieldIds, dragged)), [fieldIds, applyOrder]);

	// Column picker — opt-in. Owns the visibility model + persistence (keyed off `localStorageKey`, a
	// distinct `-columnVisibility` suffix from reorder's `-columnOrder`). Inert when no config is passed.
	const columnPickerEnabled = !!columnPicker;
	const { columnVisibilityModel, setColumnVisibilityModel, resetVisibility, resetVisibilityModel } = useColumnVisibility({
		enabled: columnPickerEnabled,
		localStorageKey,
		defaultVisibility: columnPicker?.defaultVisibility,
		onModelChange: columnPicker?.onModelChange,
	});

	// Band contiguity: with a header band AND reorder on, a column the picker reveals joins its band's
	// on-screen block instead of opening a second, split band cell wherever its slot happens to be. Only
	// picker changes go through here — MUI's own column-menu "Hide" can only hide, never reveal.
	const bandContiguityEnabled = !!headerGroups && enableColumnReorder;
	const headerGroupByField = useMemo(() => new Map(columns.flatMap((c) => (c.headerGroup ? [[String(c.field), c.headerGroup] as const] : []))), [columns]);
	const naturalFieldOrder = useMemo(() => columns.map((c) => String(c.field)), [columns]);
	const placeRevealedColumnsInBands = useCallback(
		(nextModel: GridColumnVisibilityModel) => {
			if (!bandContiguityEnabled) return;
			const placed = placeNewlyVisibleInBands({
				displayOrder: fieldIds,
				naturalOrder: naturalFieldOrder,
				headerGroupByField,
				pinnedFields: nonReorderableFields,
				previousModel: columnVisibilityModel,
				nextModel,
			});
			if (placed) applyOrder(placed);
		},
		[bandContiguityEnabled, fieldIds, naturalFieldOrder, headerGroupByField, nonReorderableFields, columnVisibilityModel, applyOrder],
	);
	const handlePickerVisibilityChange = useCallback(
		(nextModel: GridColumnVisibilityModel) => {
			placeRevealedColumnsInBands(nextModel);
			setColumnVisibilityModel(nextModel);
		},
		[placeRevealedColumnsInBands, setColumnVisibilityModel],
	);
	// Reset re-shows every default column at once, so it needs the same band placement as a toggle.
	const handlePickerVisibilityReset = useCallback(() => {
		placeRevealedColumnsInBands(resetVisibilityModel);
		resetVisibility();
	}, [placeRevealedColumnsInBands, resetVisibilityModel, resetVisibility]);

	// Column widths — opt-in. Persisted resized widths, reapplied onto the columns below so a resize survives
	// a columns-prop rebuild and page re-entry (keyed off `localStorageKey`, a distinct `-columnWidths` suffix).
	const { columnWidths, setColumnWidth, resetWidths } = useColumnWidths({ enabled: persistColumnWidths, localStorageKey });

	// Search — local state owned here, unless the caller opts into controlling it via `searchValue`
	// (see that prop's doc comment).
	const [internalSearch, setInternalSearch] = useState("");
	const search = controlledSearchValue ?? internalSearch;

	// Resetting the column order discards a user's persisted layout, so gate it behind a confirmation.
	const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
	const handleResetConfirmed = useCallback(() => {
		resetOrder();
		resetWidths();
		setResetConfirmOpen(false);
	}, [resetOrder, resetWidths]);

	// Persist a committed column resize (MUI's onColumnWidthChange fires once, on resize-stop), then forward
	// to any caller handler. Inert unless width persistence is opted in.
	const callerOnColumnWidthChange = dataGridProps?.onColumnWidthChange;
	const handleColumnWidthChange = useCallback<NonNullable<DataGridProps<T>["onColumnWidthChange"]>>(
		(params, event, details) => {
			if (persistColumnWidths) setColumnWidth(params.colDef.field, params.width);
			callerOnColumnWidthChange?.(params, event, details);
		},
		[persistColumnWidths, setColumnWidth, callerOnColumnWidthChange],
	);
	const searchEnabled = !!searchableColumns?.length;

	// A sort entry no `sortingOptions` entry can rebuild has no header and no popup row behind it — it is
	// a grid's own invisible tie-breaker. `headerClickMode="replace"` keeps those, because clearing one
	// would permanently reorder the grid with nothing in the UI to restore it.
	const retainInvisibleSortEntry = useCallback((entry: SortingState<T>) => !findMatchingSortOption(entry.sortColumns, sortingOptions), [sortingOptions]);

	// Normalize each column: strip the grid-specific `hideHeaderSeparator` flag (translating it to a
	// header class so MUI never sees the unknown field), then inject SortableDataGridHeader into
	// sortable columns that don't already have a custom header renderer.
	const effectiveColumns = useMemo<GridColDef<T>[]>(() => {
		return orderedColumns.map((col) => {
			const { hideHeaderSeparator, ...gridCol } = col;
			let next: GridColDef<T> = hideHeaderSeparator
				? { ...gridCol, headerClassName: appendHeaderClass<T>(gridCol.headerClassName, HIDE_HEADER_SEPARATOR_CLASS) }
				: gridCol;
			// Reapply a persisted resized width: it wins over the column's default `width`/`flex`, so the
			// user's choice survives this rebuild and a later page visit. Only resized columns are in the map.
			// Never onto a column that can't be resized (e.g. a row-actions column that fills the leftover width):
			// a width saved before it was locked would otherwise pin it and bring back the empty strip.
			const savedWidth = columnWidths[next.field as string];
			if (savedWidth != null && next.resizable !== false) next = { ...next, width: savedWidth, flex: undefined };
			if (sortEnabled && next.sortable !== false && !next.renderHeader) {
				const matchedOption = optionByField.get(next.field as keyof T);
				const fieldsForHeader = matchedOption?.sortColumns ?? (next.field as keyof T);
				const headerTooltip = col.headerTooltip;
				next = {
					...next,
					headerClassName: appendHeaderClass<T>(next.headerClassName, SORTABLE_HEADER_CLASS),
					// `params.colDef` is MUI's resolved column: a caller's `headerAlign`, else its type's (right for a number,
					// centre for a boolean). The stretched title box would otherwise put every unaligned label at the start.
					renderHeader: (params) => (
						<SortableDataGridHeader<T>
							field={fieldsForHeader}
							headerName={next.headerName ?? String(next.field)}
							sortState={sortHook.sortState}
							setSortState={sortHook.setSortState}
							customSortFunction={matchedOption?.customSortFunction}
							tooltipContent={headerTooltip}
							allowRemovalWhenDescendingToggle={!keepSortOnHeaderToggle}
							clickBehavior={headerClickMode}
							retainEntry={retainInvisibleSortEntry}
							align={params.colDef.headerAlign}
						/>
					),
				};
			} else if (col.headerTooltip && !next.renderHeader) {
				// Non-sortable column with a headerTooltip — inject a simple tooltip wrapper on the header label.
				const headerTooltip = col.headerTooltip;
				const headerLabel = next.headerName ?? String(next.field);
				next = {
					...next,
					renderHeader: () => (
						<InHouseTooltip title={headerTooltip} arrow placement="top">
							<Typography variant="body2" sx={{ fontWeight: 700, fontSize: "inherit", lineHeight: "inherit", cursor: "help" }}>
								{headerLabel}
							</Typography>
						</InHouseTooltip>
					),
				};
			}
			return next;
		});
	}, [
		orderedColumns,
		columnWidths,
		sortEnabled,
		sortHook.sortState,
		sortHook.setSortState,
		optionByField,
		keepSortOnHeaderToggle,
		headerClickMode,
		retainInvisibleSortEntry,
	]);

	// Reorder layer: wrap each data-column header in a drag handle. Kept separate from `effectiveColumns`
	// (and from the skeleton columns) so the handles only render in the live grid, never the skeleton.
	// No-op passthrough when reorder is off — same reference, so nothing downstream re-renders.
	const reorderableColumns = useMemo<GridColDef<T>[]>(() => {
		if (!enableColumnReorder) return effectiveColumns;
		const reorderLabel = t("system.dataGrid.reorderColumn");
		return effectiveColumns.map((col) => {
			if (col.disableReorder) return col;
			const innerRender = col.renderHeader;
			const headerLabel = col.headerName ?? String(col.field);
			const fieldStr = String(col.field);
			return {
				...col,
				renderHeader: (params) => (
					<ReorderableColumnHeader
						field={fieldStr}
						reorderLabel={reorderLabel}
						testId={dataTestId ? `${dataTestId}-reorder-handle-${fieldStr}` : undefined}
						balanceHandle={col.headerAlign === "center"}
					>
						{innerRender ? (
							innerRender(params)
						) : (
							<Typography variant="body2" sx={{ fontWeight: 700, fontSize: "inherit", lineHeight: "inherit" }}>
								{headerLabel}
							</Typography>
						)}
					</ReorderableColumnHeader>
				),
			};
		});
	}, [enableColumnReorder, effectiveColumns, t, dataTestId]);

	// dnd-kit plumbing for column reorder. Sensors/handlers are created unconditionally (hook rules);
	// the providers are only mounted around the live grid when the feature is on (see `withColumnReorder`).
	const reorderSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor));
	// Wraps the live grid; used to scope header-cell queries for the imperative drop indicator.
	const reorderContainerRef = useRef<HTMLDivElement | null>(null);
	const clearColumnDropMarkers = useCallback(() => {
		const root = reorderContainerRef.current;
		if (!root) return;
		for (const el of root.querySelectorAll(`.${COLUMN_DROP_LEFT_CLASS}, .${COLUMN_DROP_RIGHT_CLASS}`)) {
			el.classList.remove(COLUMN_DROP_LEFT_CLASS, COLUMN_DROP_RIGHT_CLASS);
		}
	}, []);
	// Paint the drop indicator on the hovered header CELL (flush on the boundary the column will land
	// against), imperatively — avoids re-rendering the whole grid on every drag-over.
	const handleColumnDragOver = useCallback(
		(event: DragOverEvent) => {
			const root = reorderContainerRef.current;
			if (!root) return;
			clearColumnDropMarkers();
			const { active, over } = event;
			if (!over || active.id === over.id) return;
			const activeIdx = reorderableFieldIds.indexOf(String(active.id));
			const overIdx = reorderableFieldIds.indexOf(String(over.id));
			if (activeIdx === -1 || overIdx === -1) return;
			const overCell = root.querySelector(`.MuiDataGrid-columnHeader[data-field="${CSS.escape(String(over.id))}"]`);
			// Dragging rightward (active before over) → lands after `over` (right edge); else before (left edge).
			overCell?.classList.add(activeIdx < overIdx ? COLUMN_DROP_RIGHT_CLASS : COLUMN_DROP_LEFT_CLASS);
		},
		[reorderableFieldIds, clearColumnDropMarkers],
	);
	const handleColumnDragEnd = useCallback(
		(event: DragEndEvent) => {
			clearColumnDropMarkers();
			const { active, over } = event;
			if (!over || active.id === over.id) return;
			moveField(String(active.id), String(over.id));
		},
		[moveField, clearColumnDropMarkers],
	);
	const withColumnReorder = useCallback(
		(node: ReactNode): ReactNode =>
			enableColumnReorder ? (
				<DndContext
					sensors={reorderSensors}
					collisionDetection={closestCenter}
					onDragOver={handleColumnDragOver}
					onDragEnd={handleColumnDragEnd}
					onDragCancel={clearColumnDropMarkers}
				>
					<SortableContext items={reorderableFieldIds} strategy={horizontalListSortingStrategy}>
						<Box ref={reorderContainerRef} sx={{ display: "contents" }}>
							{node}
						</Box>
					</SortableContext>
				</DndContext>
			) : (
				node
			),
		[enableColumnReorder, reorderSensors, handleColumnDragOver, handleColumnDragEnd, clearColumnDropMarkers, reorderableFieldIds],
	);

	const filteredRows = useMemo(() => {
		if (!searchEnabled) return rows;
		const q = search.trim().toLowerCase();
		if (!q) return rows;
		const specs = searchableColumns ?? [];
		return rows.filter((r) => specs.some((spec) => extractSearchValue(spec, r).toLowerCase().includes(q)));
	}, [rows, search, searchEnabled, searchableColumns]);

	// Only a grid with pins reads its row ids here, so one without them (often passing an inline `getRowId`) doesn't
	// re-sort on every render.
	const pinnedGetRowId = getRowSortPin ? getRowId : null;
	const displayRows = useMemo(() => {
		if (!sortEnabled) return filteredRows;
		if (getRowSortPin && pinnedGetRowId) return sortRowsWithPins(filteredRows, sortHook.sortRows, pinnedGetRowId, getRowSortPin);
		return sortHook.sortRows(filteredRows);
	}, [filteredRows, sortEnabled, sortHook.sortRows, getRowSortPin, pinnedGetRowId]);

	// ── Row grouping ────────────────────────────────────────────────────────────────────────────
	// Runs on `displayRows`, i.e. AFTER search + sort: a band only exists for rows that survived the
	// search, and rows keep the order the sort gave them inside their band.
	const groupingRequested = rowGrouping != null;
	// MUI's `__check__` column would occupy the first cell — the one the band col-spans from — so the
	// two can't both own it. Say so loudly rather than rendering a band that starts one column in.
	const checkboxSelectionOn = !!dataGridProps?.checkboxSelection;
	// In an effect, not the render body: a misconfiguration is reported once per configuration change
	// rather than once per render — a render-body console.error re-fires on every keystroke in the
	// search box and buries the console it is trying to warn in.
	useEffect(() => {
		if (import.meta.env.DEV && groupingRequested && checkboxSelectionOn) {
			console.error("SystemDataGrid: `rowGrouping` and `dataGridProps.checkboxSelection` cannot be combined — grouping is disabled for this grid.");
		}
	}, [groupingRequested, checkboxSelectionOn]);
	// Bands are injected as real rows, so a finite page size counts them against the caller's rows and
	// silently strands the tail: 8 groups over 50 slots leaves room for ~42 jobs and no footer to reach
	// the rest. The fix is the caller's (`pageSize: -1` plus `hideFooter`), so warn rather than override
	// a page size someone may have chosen deliberately.
	// Read the page size from the same sources, in the same order, as whichever model will actually be
	// live: an expandable grid drives a controlled model from `defaultPageSize ?? initialState` (below),
	// every other grid hands MUI the caller's own `paginationModel`/`initialState` untouched.
	const expansionRequested = isRowExpansion(props) || isCellExpansion(props);
	const callerInitialPageSize = dataGridProps?.initialState?.pagination?.paginationModel?.pageSize;
	const livePageSize = expansionRequested
		? (defaultPageSize ?? callerInitialPageSize ?? DEFAULT_PAGE_SIZE)
		: (dataGridProps?.paginationModel?.pageSize ?? callerInitialPageSize ?? DEFAULT_PAGE_SIZE);
	const finitePageSize = livePageSize > 0 && !!dataGridProps?.hideFooter;
	useEffect(() => {
		if (import.meta.env.DEV && groupingRequested && finitePageSize) {
			console.error(
				"SystemDataGrid: `rowGrouping` with a finite page size and `hideFooter` strands rows past the first page — group bands consume page slots. Pass `pageSizeOptions: [{ value: -1 }]` and `initialState.pagination.paginationModel.pageSize: -1`.",
			);
		}
	}, [groupingRequested, finitePageSize]);
	const groupingEnabled = groupingRequested && !checkboxSelectionOn && !loading && !error;
	// DEFAULT: no `defaultCollapsedKeys` → every band starts open.
	const [internalCollapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set(rowGrouping?.defaultCollapsedKeys ?? []));
	// Controlled mode: the caller's `collapsedKeys` IS the fold state. Memoized on the keys' content, not
	// the array's identity, so a caller that rebuilds the array each render (`[...set]`) doesn't re-band
	// every row and re-evaluate every column on every render.
	const controlledCollapse = rowGrouping && isControlledRowGroupCollapse(rowGrouping) ? rowGrouping : null;
	const controlledCollapsedSignature = controlledCollapse ? controlledCollapse.collapsedKeys.join("\u0000") : null;
	const controlledCollapsedKeysRef = useLatestRef(controlledCollapse?.collapsedKeys ?? null);
	const onCollapsedKeysChangeRef = useLatestRef(controlledCollapse?.onCollapsedKeysChange ?? null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: `controlledCollapsedSignature` stands in for the keys array (see above).
	const collapsedGroups = useMemo(
		() => (controlledCollapse ? new Set(controlledCollapse.collapsedKeys) : internalCollapsedGroups),
		[controlledCollapsedSignature, internalCollapsedGroups],
	);
	// Stable across renders (it reads the latest controlled keys and callback through refs), so a new
	// `collapsedKeys` array or an inline `onCollapsedKeysChange` never re-creates it — and so never, on
	// its own, re-creates the band renderer and hands MUI a new `columns` identity.
	const toggleGroup = useCallback(
		(key: string) => {
			const controlledKeys = controlledCollapsedKeysRef.current;
			const onKeysChange = onCollapsedKeysChangeRef.current;
			if (controlledKeys && onKeysChange) {
				// Controlled: ask for the next set; the band folds only when the caller hands it back.
				onKeysChange(controlledKeys.includes(key) ? controlledKeys.filter((k) => k !== key) : [...controlledKeys, key]);
				return;
			}
			setCollapsedGroups((prev) => {
				const next = new Set(prev);
				if (!next.delete(key)) next.add(key);
				return next;
			});
		},
		[controlledCollapsedKeysRef, onCollapsedKeysChangeRef],
	);
	// An active search reads as fully expanded, so a folded band can never hide its own match. The
	// user's fold state is kept, not cleared — it comes back when the search box is emptied.
	const searchActive = searchEnabled && search.trim().length > 0;

	// Bandless groups render their rows with no band above them, so nothing can fold them: they are
	// forced open regardless of `defaultCollapsedKeys` or a stale toggle.
	const bandlessKeys = rowGrouping?.bandlessKeys;
	const bandlessSet = useMemo(() => new Set(bandlessKeys ?? []), [bandlessKeys]);

	/**
	 * Ordered buckets of the displayed rows, plus each band's collapsed state. Empty when grouping is off.
	 * A `persistentGroupKeys` group no row landed in is included with `rows: []` (the empty band) — except
	 * during a search or over a grid with no rows at all, where it would only add noise (see the prop docs).
	 */
	const rowGroups = useMemo<RowGroup<T>[]>(() => {
		if (!groupingEnabled || !rowGrouping) return [];
		const buckets = new Map<string, T[]>();
		for (const row of displayRows) {
			const key = rowGrouping.getGroupKey(row);
			const bucket = buckets.get(key);
			if (bucket) bucket.push(row);
			else buckets.set(key, [row]);
		}
		const showEmptyBands = !searchActive && displayRows.length > 0;
		const emptyKeys = showEmptyBands ? (rowGrouping.persistentGroupKeys ?? []).filter((key) => !buckets.has(key) && !bandlessSet.has(key)) : [];
		const known = new Set([...buckets.keys(), ...emptyKeys]);
		// `sortWithinGroups`: the bands' order is the rows' order before the sort, so sorting moves rows, never bands.
		const firstRowOrder = rowGrouping.sortWithinGroups
			? [...new Set(filteredRows.map((row) => rowGrouping.getGroupKey(row)))].filter((key) => buckets.has(key))
			: [...buckets.keys()];
		// Caller-pinned keys first (in their order), then whatever else appeared, in first-row order, then
		// any unpinned empty bands in the order the caller listed them.
		const pinned = (rowGrouping.groupOrder ?? []).filter((key) => known.has(key));
		const rest = [...firstRowOrder, ...emptyKeys].filter((key) => !pinned.includes(key));
		return [...pinned, ...new Set(rest)].map((key) => {
			const rows = buckets.get(key) ?? [];
			return {
				key,
				rows,
				// An empty band has nothing to fold, so a stale toggle or `defaultCollapsedKeys` can't mark it folded.
				collapsed: rows.length > 0 && !searchActive && !bandlessSet.has(key) && collapsedGroups.has(key),
			};
		});
	}, [groupingEnabled, rowGrouping, displayRows, filteredRows, collapsedGroups, searchActive, bandlessSet]);

	/** `displayRows` with a band row inserted before each banded bucket, and collapsed buckets' rows dropped. */
	const groupedRows = useMemo<GridRow<T>[]>(() => {
		if (!groupingEnabled) return displayRows;
		const out: GridRow<T>[] = [];
		for (const group of rowGroups) {
			if (!bandlessSet.has(group.key))
				out.push(group.rows.length > 0 ? { __groupKey: group.key, __group: group } : { __groupKey: group.key, __group: group, __groupEmpty: true });
			if (!group.collapsed) out.push(...group.rows);
		}
		return out;
	}, [groupingEnabled, displayRows, rowGroups, bandlessSet]);

	// Collapse state lives here, but a caller with a header-level bulk action ("apply to every row
	// shown") needs it: a folded band's rows survived its filters and are still NOT on screen. Derived
	// from `rowGroups`, so it reports the EFFECTIVE fold state — empty while a search force-expands
	// every band — rather than the raw toggle set.
	const onCollapsedChange = rowGrouping?.onCollapsedChange;
	const effectiveCollapsedKeys = useMemo(() => rowGroups.filter((group) => group.collapsed).map((group) => group.key), [rowGroups]);
	// `effectiveCollapsedKeys` gets a new identity whenever rows change; its *signature* changes only
	// when a band actually folds or unfolds, so the caller is notified once per real transition and a
	// caller that re-derives its rows from this can't feed a render loop back in.
	// NUL-joined because a group key is caller data and routinely contains spaces ("Nursing Admin").
	const collapsedSignature = effectiveCollapsedKeys.join("\u0000");
	const lastCollapsedSignatureRef = useRef<string | null>(null);
	useEffect(() => {
		if (!onCollapsedChange || lastCollapsedSignatureRef.current === collapsedSignature) return;
		lastCollapsedSignatureRef.current = collapsedSignature;
		onCollapsedChange(effectiveCollapsedKeys);
	}, [onCollapsedChange, collapsedSignature, effectiveCollapsedKeys]);

	// Export — opt-in. Serializes the displayed rows (search + sort applied) in the visible column order
	// (`orderedColumns` reflects any reorder; the injected expand column isn't part of it).
	const exportEnabled = !!exportOptions;
	const handleExport = useCallback(
		(format: ExportFormat) => {
			if (!exportOptions) return;
			// Respect the user's column visibility ("Manage columns"): export only currently-visible columns.
			const visibilityModel = apiRef.current ? gridColumnVisibilityModelSelector(apiRef) : {};
			const visibleColumns = orderedColumns.filter((col) => visibilityModel[col.field as string] !== false);
			if (format === "xlsx") void exportRowsAsXlsx(displayRows, visibleColumns, exportOptions.fileName);
			else exportRowsAsCsv(displayRows, visibleColumns, exportOptions.fileName);
		},
		[exportOptions, displayRows, orderedColumns, apiRef],
	);

	const expandActive = !loading && !error;
	const rowExpandEnabled = expandActive && isRowExpansion(props);
	const cellExpandEnabled = expandActive && isCellExpansion(props);
	const expandEnabled = rowExpandEnabled || cellExpandEnabled;
	if (import.meta.env.DEV && isRowExpansion(props) && isCellExpansion(props)) {
		// Dev-time invariant: the union forbids both modes at compile time, but `as`/spread can bypass it
		// (the dev fixture spreads an `ExpansionProps`). Surface the misuse loudly instead of silently
		// favoring row mode.
		console.error(
			"SystemDataGrid: both getRowExpandContent and getCellExpandContent were provided. These modes are mutually exclusive — falling back to row-level expansion.",
		);
	}
	// Three pieces of expansion state, each with a distinct job:
	//  • expandedIds      — open intent (drives the chevron + the <Collapse> `in`).
	//  • mountedDetailIds — detail rows still in the DOM. A *closing* row stays here until its exit
	//                       animation finishes, then unmounts — you can't animate a row you've removed.
	//  • transitioningIds — rows currently animating open/closed. While transitioning the detail row is
	//                       sized "auto" so it SLIDES with the Collapse; once settled it switches to the
	//                       fixed `getExpandContentHeight` (no ResizeObserver → no steady-state scroll loop).
	//  • pendingExpandIds  — lazy rows (see `onRowExpandRequest`) whose content is being fetched: the
	//                       chevron shows a spinner and the row stays CLOSED until the content lands.
	//                       Grid-owned, not a caller prop: it has to join `rowsWithDetails`' deps to
	//                       repaint the cell, the same trick `expandedIds` uses below.
	// Controlled open rows (`expandedRowIds` + `onExpandedRowIdsChange`, row mode only). The prop is the open
	// intent; the three sets below still run the lifecycle, and a reconcile effect (below) walks them to the
	// prop through the same open/close path a click takes, so a controlled change animates like a click.
	// Seeded from the prop, so rows that are open on mount render open with no entrance animation.
	const controlledExpansion = isControlledRowExpansion(props) ? props : null;
	// Null on an uncontrolled grid: the grid owns its open rows, and the request layer applies a toggle at once.
	const controlledExpandedIdsRef = useLatestRef(controlledExpansion === null ? null : controlledExpansion.expandedRowIds);
	const onExpandedRowIdsChangeRef = useLatestRef(controlledExpansion === null ? null : controlledExpansion.onExpandedRowIdsChange);
	// Compared by content, so a caller that rebuilds the array every render doesn't re-run the reconcile.
	const controlledExpandedSignature = controlledExpansion ? controlledExpansion.expandedRowIds.map(String).join("\u0000") : null;
	const [expandedIds, setExpandedIds] = useState<Set<string | number>>(() => new Set(controlledExpansion?.expandedRowIds));
	const [mountedDetailIds, setMountedDetailIds] = useState<Set<string | number>>(() => new Set(controlledExpansion?.expandedRowIds));
	const [transitioningIds, setTransitioningIds] = useState<Set<string | number>>(() => new Set());
	const [pendingExpandIds, setPendingExpandIds] = useState<Set<string | number>>(() => new Set());
	const expandedIdsRef = useRef(expandedIds);
	expandedIdsRef.current = expandedIds;
	// Mirror the open rows OUT of the grid (see `onExpandedRowsChange`). Fires on mount with `[]` and on
	// every real open/close — `expandedIds` is only ever replaced with a new Set when it actually changes,
	// so this cannot re-fire on an unrelated render. Kept in a ref so a caller's inline arrow is not a
	// re-run trigger.
	const onExpandedRowsChangeRef = useRef(onExpandedRowsChange);
	onExpandedRowsChangeRef.current = onExpandedRowsChange;
	useEffect(() => {
		onExpandedRowsChangeRef.current?.([...expandedIds]);
	}, [expandedIds]);
	// Not a per-render mirror like the others: the authoritative copy, written synchronously by
	// `armPendingExpand`/`dropPendingExpand` before the matching `setState`, so two clicks dispatched in
	// one task can't both read "not pending".
	const pendingExpandIdsRef = useRef(pendingExpandIds);
	// Started-and-unsettled lazy requests, `rowId → token`. Separate from `pendingExpandIds` because that
	// is what the user SEES and may be cleared early (row left `rows`, stopped being expandable), while
	// this is what actually gates firing a second request.
	const inFlightExpandRequestsRef = useRef<Map<string | number, number>>(new Map());
	const expandRequestTokenRef = useRef(0);
	// Text of the polite live region rendered at the bottom of the grid (lazy grids only) — see there.
	const [expandStatusMessage, setExpandStatusMessage] = useState("");
	// The live `rows` behind a stable identity, so the lazy-open handler can find the clicked row without
	// making every `rows` change recompute `columnsWithExpand`.
	const rowsRef = useRef(rows);
	rowsRef.current = rows;
	// Read in renderCell (via ref so `columnsWithExpand` stays stable) to decide whether a detail row
	// should animate on mount — only when it's mid-toggle, not when virtualization re-mounts it.
	const transitioningIdsRef = useRef(transitioningIds);
	transitioningIdsRef.current = transitioningIds;
	const [activeFieldByRow, setActiveFieldByRow] = useState<Map<string | number, keyof T>>(() => new Map());
	const activeFieldByRowRef = useRef(activeFieldByRow);
	activeFieldByRowRef.current = activeFieldByRow;

	// One pending timer per detail id. Cleared/replaced on any re-toggle so a quick open→close→open
	// doesn't leave a stale timer that unmounts a row that's open again.
	const animationTimers = useRef<Map<string | number, ReturnType<typeof setTimeout>>>(new Map());
	const clearDetailTimer = useCallback((id: string | number) => {
		const timer = animationTimers.current.get(id);
		if (timer != null) {
			clearTimeout(timer);
			animationTimers.current.delete(id);
		}
	}, []);
	// After the animation completes: drop the row from the DOM (close) and end its transition. Timer-based
	// (not Collapse `onExited`) so it fires even when the row is scrolled out of view — e.g. collapse-all.
	const scheduleUnmount = useCallback(
		(id: string | number) => {
			clearDetailTimer(id);
			animationTimers.current.set(
				id,
				setTimeout(() => {
					animationTimers.current.delete(id);
					setMountedDetailIds((prev) => {
						if (!prev.has(id)) return prev;
						const next = new Set(prev);
						next.delete(id);
						return next;
					});
					setTransitioningIds((prev) => {
						if (!prev.has(id)) return prev;
						const next = new Set(prev);
						next.delete(id);
						return next;
					});
					setActiveFieldByRow((prev) => {
						// Cell mode: drop the active field only now (at unmount), not on the close click — so the
						// closing detail keeps rendering its last content through the exit animation. No-op in row mode.
						if (!prev.has(id)) return prev;
						const next = new Map(prev);
						next.delete(id);
						return next;
					});
				}, DETAIL_ANIMATION_MS),
			);
		},
		[clearDetailTimer],
	);
	const scheduleSettle = useCallback(
		// After an open/swap animation completes: end the transition (drop from transitioningIds) so the row
		// settles from "auto" to its fixed height. One timer per id (replaces any in-flight one), so a quick
		// re-toggle/re-swap just resets the clock instead of stacking timers.
		(id: string | number) => {
			clearDetailTimer(id);
			animationTimers.current.set(
				id,
				setTimeout(() => {
					animationTimers.current.delete(id);
					setTransitioningIds((prev) => {
						if (!prev.has(id)) return prev;
						const next = new Set(prev);
						next.delete(id);
						return next;
					});
				}, DETAIL_ANIMATION_MS),
			);
		},
		[clearDetailTimer],
	);
	// Open: mount + open intent; the row is auto-height while transitioning, then settles to fixed. A no-op on
	// an open row, so the auto-expand and lazy-drain effects and the controlled reconcile can't flip one shut.
	const expandOnly = useCallback(
		(id: string | number) => {
			if (expandedIdsRef.current.has(id)) return;
			setTransitioningIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
			setExpandedIds((prev) => new Set(prev).add(id));
			setMountedDetailIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
			scheduleSettle(id);
		},
		[scheduleSettle],
	);
	// Close: drop the open intent (Collapse animates shut), keep it mounted, unmount on timer. No-op on a closed row.
	const closeOnly = useCallback(
		(id: string | number) => {
			if (!expandedIdsRef.current.has(id)) return;
			setTransitioningIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
			setExpandedIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
			scheduleUnmount(id);
		},
		[scheduleUnmount],
	);
	const toggleExpand = useCallback((id: string | number) => (expandedIdsRef.current.has(id) ? closeOnly(id) : expandOnly(id)), [closeOnly, expandOnly]);
	// The request layer. Uncontrolled, each applies at once; controlled, each only ASKS the caller for the next
	// set (built from the caller's own ids, like `toggleGroup`) and the reconcile effect applies what comes back.
	// Stable: they read the controlled ids and callback through refs.
	const requestToggleRow = useCallback(
		(id: string | number) => {
			const ids = controlledExpandedIdsRef.current;
			const onIdsChange = onExpandedRowIdsChangeRef.current;
			if (ids && onIdsChange) {
				onIdsChange(ids.includes(id) ? ids.filter((openId) => openId !== id) : [...ids, id]);
				return;
			}
			toggleExpand(id);
		},
		[controlledExpandedIdsRef, onExpandedRowIdsChangeRef, toggleExpand],
	);
	const requestOpenRows = useCallback(
		(toOpen: (string | number)[]) => {
			const ids = controlledExpandedIdsRef.current;
			const onIdsChange = onExpandedRowIdsChangeRef.current;
			if (ids && onIdsChange) {
				const added = toOpen.filter((id) => !ids.includes(id));
				if (added.length > 0) onIdsChange([...ids, ...added]);
				return;
			}
			for (const id of toOpen) expandOnly(id);
		},
		[controlledExpandedIdsRef, onExpandedRowIdsChangeRef, expandOnly],
	);
	// The only two writers of `pendingExpandIds`: mutate the ref first, then push the same Set into state
	// to trigger the repaint. Deriving from the ref rather than `prev` is safe because nothing else writes.
	const armPendingExpand = useCallback((id: string | number) => {
		// An unchanged live-region text is not re-announced, so clearing it here is what makes the
		// SECOND load announce at all.
		setExpandStatusMessage("");
		if (pendingExpandIdsRef.current.has(id)) return;
		const next = new Set(pendingExpandIdsRef.current).add(id);
		pendingExpandIdsRef.current = next;
		setPendingExpandIds(next);
	}, []);
	const dropPendingExpand = useCallback((ids: Iterable<string | number>) => {
		const next = new Set(pendingExpandIdsRef.current);
		let changed = false;
		for (const id of ids) changed = next.delete(id) || changed;
		if (!changed) return;
		pendingExpandIdsRef.current = next;
		setPendingExpandIds(next);
	}, []);
	/**
	 * Called when an `onRowExpandRequest` promise settles. Token-gated: a settling request that no longer
	 * owns its row's in-flight entry touches neither that entry nor the spinner state. Today the
	 * duplicate guard already makes two concurrent requests for one row impossible, so this is defence
	 * in depth for a future second start path (cancel-and-retry, caller-driven refresh).
	 */
	const settleExpandRequest = useCallback(
		(id: string | number, token: number, started: boolean) => {
			if (inFlightExpandRequestsRef.current.get(id) !== token) return;
			inFlightExpandRequestsRef.current.delete(id);
			// `started === true` keeps the spinner: the content is on its way and the drain effect owns the
			// open. `false` means nothing will arrive, so hand the chevron back for a retry.
			if (!started) dropPendingExpand([id]);
		},
		[dropPendingExpand],
	);
	/**
	 * The chevron's click handler in row mode. Outcomes, in order:
	 *  • pending               → ignored; the chevron is a loading indicator, not a cancel button.
	 *  • open / content there  → the existing synchronous close / open.
	 *  • request already in flight (spinner cleared early) → re-arm the spinner, fire nothing.
	 *  • lazy row              → mark pending, ask the caller to fetch, and DON'T open. The drain effect
	 *    below owns the open, once content is actually there — never off the promise's fulfilment, which
	 *    lands before the caller's `setState` has rendered.
	 *  • content null, no handler → nothing, rather than flip the glyph open over an empty detail area.
	 */
	const handleRowExpandToggle = useCallback(
		(id: string | number) => {
			if (pendingExpandIdsRef.current.has(id)) return;
			if (expandedIdsRef.current.has(id)) {
				requestToggleRow(id);
				return;
			}
			const row = rowsRef.current.find((candidate) => getRowId(candidate) === id);
			// A click that lands after its row left `rows` (a refetch between paint and click). Opening it
			// would put a phantom id into `expandedIds`/`mountedDetailIds` that nothing can close, and that
			// would auto-open the row if that id ever came back.
			if (row == null) return;
			// Same race, one step later: the row is still here but the caller's predicate has gone false
			// since it was painted. Re-check rather than trust the rendered chevron, so a stale click can't
			// open — or fire a request for — a row that is no longer expandable. The close path for an open
			// row returned above, so it keeps working even when the predicate flips underneath it.
			if (isRowExpandable && !isRowExpandable(row)) return;
			if (getRowExpandContent?.(row) != null) {
				requestToggleRow(id);
				return;
			}
			if (!onRowExpandRequest) return;
			if (inFlightExpandRequestsRef.current.has(id)) {
				armPendingExpand(id);
				return;
			}
			const token = ++expandRequestTokenRef.current;
			inFlightExpandRequestsRef.current.set(id, token);
			armPendingExpand(id);
			// The contract is that this handler resolves `false` rather than failing. Both ways of breaking
			// it are caught — a rejected promise, and a synchronous throw that `.catch` would never see —
			// since either would strand the in-flight entry and spinner with no way to retry.
			const reportBrokenContract = (requestError: unknown) => {
				settleExpandRequest(id, token, false);
				if (import.meta.env.DEV) console.error("SystemDataGrid: onRowExpandRequest failed instead of resolving false.", requestError);
			};
			try {
				onRowExpandRequest(row)
					.then((started) => settleExpandRequest(id, token, started))
					.catch(reportBrokenContract);
			} catch (requestError: unknown) {
				reportBrokenContract(requestError);
			}
		},
		[onRowExpandRequest, isRowExpandable, getRowExpandContent, getRowId, requestToggleRow, armPendingExpand, settleExpandRequest],
	);
	const collapseAll = useCallback(() => {
		// Deliberately does NOT touch `pendingExpandIds`: collapse-all closes what is OPEN, and a pending
		// row is closed by definition, so it has nothing to collapse. The alternative (clearing pending)
		// would be a lie — the grid cannot cancel a caller's request, so the spinner would vanish while
		// the fetch kept running, and the row would then silently refuse to open when its content landed.
		const open = expandedIdsRef.current;
		if (open.size === 0) return;
		setTransitioningIds((prev) => {
			const next = new Set(prev);
			for (const id of open) next.add(id);
			return next;
		});
		for (const id of open) scheduleUnmount(id);
		setExpandedIds(() => new Set());
	}, [scheduleUnmount]);
	// Read through a ref so `columnsWithExpand` (which hosts the header button) doesn't rebuild on every search keystroke.
	const displayRowsForExpandRef = useRef(displayRows);
	displayRowsForExpandRef.current = displayRows;
	// Opens every displayed row whose content is already there — never a lazy row, which would fire a fetch per row.
	const expandAll = useCallback(() => {
		for (const row of displayRowsForExpandRef.current) {
			if (getRowExpandContent?.(row) != null) expandOnly(getRowId(row));
		}
	}, [getRowExpandContent, getRowId, expandOnly]);
	const requestCollapseAll = useCallback(() => {
		const onIdsChange = onExpandedRowIdsChangeRef.current;
		if (controlledExpandedIdsRef.current && onIdsChange) {
			onIdsChange([]);
			return;
		}
		collapseAll();
	}, [controlledExpandedIdsRef, onExpandedRowIdsChangeRef, collapseAll]);

	// Controlled reconcile: walk the lifecycle sets to the caller's ids through `expandOnly`/`closeOnly`, the path
	// a click takes, so the open/close animation is kept. A layout effect, so the frame between the new prop and
	// the reconciled sets is never painted.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `controlledExpandedSignature` stands in for the ids array (read through the ref).
	useLayoutEffect(() => {
		const ids = controlledExpandedIdsRef.current;
		if (ids === null) return;
		const target = new Set(ids);
		for (const id of expandedIdsRef.current) if (!target.has(id)) closeOnly(id);
		for (const id of target) expandOnly(id);
	}, [controlledExpandedSignature, controlledExpandedIdsRef, closeOnly, expandOnly]);

	// Auto-expand: a non-empty search that narrows to exactly one (expandable) row opens it without
	// waiting for a chevron click. `expandOnly` already no-ops on an already-open row, so this stays
	// idempotent across the re-renders `filteredRows` triggers on every keystroke.
	// The `getRowExpandContent == null` bail is also what keeps LAZY rows out of it: searching must
	// never silently fire an `onRowExpandRequest` (by construction an expensive one) on the user's behalf.
	useEffect(() => {
		if (!autoExpandSingleSearchResult || !rowExpandEnabled || !searchEnabled || !search.trim() || filteredRows.length !== 1) return;
		const [row] = filteredRows;
		if (getRowExpandContent?.(row) == null) return;
		const id = getRowId(row);
		// Controlled: ask once, not on every keystroke that keeps the single match (the ids already hold it).
		if (expandedIdsRef.current.has(id)) return;
		requestOpenRows([id]);
	}, [autoExpandSingleSearchResult, rowExpandEnabled, searchEnabled, search, filteredRows, getRowExpandContent, getRowId, requestOpenRows]);

	// Drains `pendingExpandIds` — the load-then-expand half of `onRowExpandRequest`. A pending row opens
	// the moment its content becomes available (that render, not the promise's fulfilment), and is
	// dropped without opening if it left `rows` or stopped being expandable. Reads the caller's `rows`,
	// not the searched view, so a search typed mid-resolve doesn't strand a pending row. A filter change
	// is different: it flips `loading`, so the spinner is dropped and the resolved row simply gets a
	// normal chevron — a spinner can't sit on a skeleton grid.
	// Dropping retires the SPINNER only, never the in-flight entry, so no second request can start.
	useEffect(() => {
		if (pendingExpandIds.size === 0) return;
		if (!rowExpandEnabled) {
			dropPendingExpand(pendingExpandIds);
			return;
		}
		const rowById = new Map(rows.map((row) => [getRowId(row), row]));
		const toOpen: Array<string | number> = [];
		const toDrop: Array<string | number> = [];
		for (const id of pendingExpandIds) {
			const row = rowById.get(id);
			if (!row || (isRowExpandable ? !isRowExpandable(row) : false)) {
				toDrop.push(id);
				continue;
			}
			if (getRowExpandContent?.(row) != null) toOpen.push(id);
		}
		if (toOpen.length === 0 && toDrop.length === 0) return;
		if (toOpen.length > 0) requestOpenRows(toOpen);
		// The one moment worth announcing: a row the user asked for has finished loading and is now open.
		if (toOpen.length > 0) setExpandStatusMessage(t("system.dataGrid.expandRowLoaded"));
		dropPendingExpand([...toOpen, ...toDrop]);
	}, [pendingExpandIds, rows, rowExpandEnabled, isRowExpandable, getRowExpandContent, getRowId, requestOpenRows, dropPendingExpand, t]);

	// The single seam that knows about the active field: pick row-level vs cell-level content/height.
	// Both read `activeFieldByRowRef.current` (not state) so they stay referentially stable across a
	// field swap — the swap re-render is driven by `transitioningIds`/`expandedIds`, as in row mode.
	const resolveDetailContent = useCallback(
		(row: T): ReactNode | null => {
			if (cellExpandEnabled) {
				const field = activeFieldByRowRef.current.get(getRowId(row));
				return field == null ? null : (getCellExpandContent?.(row, field) ?? null);
			}
			return getRowExpandContent?.(row) ?? null;
		},
		[cellExpandEnabled, getCellExpandContent, getRowExpandContent, getRowId],
	);
	const resolveDetailHeight = useCallback(
		(row: T): number => {
			if (cellExpandEnabled) {
				const field = activeFieldByRowRef.current.get(getRowId(row));
				return field == null ? 0 : (getCellExpandContentHeight?.(row, field) ?? 0);
			}
			return getExpandContentHeight?.(row) ?? 0;
		},
		[cellExpandEnabled, getCellExpandContentHeight, getExpandContentHeight, getRowId],
	);

	// Cell-expand entry point. Reuses `toggleExpand`/`scheduleUnmount` for the open/close lifecycle —
	// only the active-field bookkeeping and the in-place swap motion are new.
	const handleCellExpand = useCallback(
		(id: string | number, field: keyof T) => {
			const isOpen = expandedIdsRef.current.has(id);
			const activeField = activeFieldByRowRef.current.get(id);
			if (isOpen && activeField === field) {
				// Close same cell: normal close path. The field entry is cleared at unmount (in scheduleUnmount)
				// so the exit animation keeps rendering this content.
				toggleExpand(id);
				return;
			}
			if (isOpen && activeField !== field) {
				// Swap in place: keep the row open + mounted, re-point the field, and re-enter the transition so
				// it slides from the current to the new content height (same settle as the open path).
				setActiveFieldByRow((prev) => new Map(prev).set(id, field));
				setTransitioningIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
				scheduleSettle(id);
				return;
			}
			// Open: set the field first so resolveDetailContent has it on the first render, then open.
			setActiveFieldByRow((prev) => new Map(prev).set(id, field));
			toggleExpand(id);
		},
		[toggleExpand, scheduleSettle],
	);
	// Switch a row's height mode (auto while transitioning ↔ fixed when settled) by asking the grid to
	// re-read getRowHeight. Add/remove of rows already triggers a recompute; this covers the in-place
	// auto→fixed (open settle) and fixed→auto (close start) flips where the rendered row set is unchanged.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `transitioningIds` is a trigger-only dep — not read here, it just fires this effect so the grid re-reads getRowHeight; `apiRef` is a stable ref.
	useEffect(() => {
		if (expandEnabled) apiRef.current?.resetRowHeights?.();
	}, [transitioningIds, expandEnabled]);
	// Clear any pending animation timers on unmount.
	useEffect(() => {
		const timers = animationTimers.current;
		return () => {
			for (const timer of timers.values()) clearTimeout(timer);
			timers.clear();
		};
	}, []);
	const hasExpandedRows = expandEnabled && expandedIds.size > 0;
	// What the chevrons and detail cells draw from (`RowExpansionStateContext`), so they follow an open or close
	// without their cells re-rendering.
	const rowExpansionState = useMemo(
		() => ({ expanded: expandedIds, transitioning: transitioningIds, pending: pendingExpandIds }),
		[expandedIds, transitioningIds, pendingExpandIds],
	);
	// The sets that, unless `stableRowsOnToggle`, hand MUI fresh rows on every open, close and pending change (see
	// `rowsWithDetails`). Null under it: the cells read them from context, and the rows change only with
	// `mountedDetailIds`.
	const toggleRefreshExpanded = stableRowsOnToggle ? null : expandedIds;
	const toggleRefreshPending = stableRowsOnToggle ? null : pendingExpandIds;
	// With `hideCollapseAllRowsHeader` this stays false, so opening the first row or closing the last never
	// rebuilds the column set (the header renderer is one of its inputs).
	const showCollapseAllHeader = hasExpandedRows && !hideCollapseAllRowsHeader;

	// Controlled pagination (only meaningful when expandEnabled — see DEFAULT_PAGE_SIZE comment).
	//
	// `dataGridProps.initialState.pagination` is read as a fallback because the expansion branch passes
	// `initialState={undefined}` to MUI (a controlled model and an initialState conflict), which used to
	// DISCARD a caller's page size in silence: a grid configured `pageSize: -1` ("All") kept every row
	// the moment it also gained expansion, then stranded everything past 50 behind a footer it had
	// hidden. `defaultPageSize` still wins where both are set.
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(() => ({
		page: dataGridProps?.initialState?.pagination?.paginationModel?.page ?? 0,
		pageSize: defaultPageSize ?? dataGridProps?.initialState?.pagination?.paginationModel?.pageSize ?? DEFAULT_PAGE_SIZE,
	}));
	const effectivePaginationModel = useMemo<GridPaginationModel>(() => {
		// Clamp the page if the real-row count shrank (e.g. a search) so we never slice past the end.
		// Returns the same object reference when already in range, keeping it stable for memo deps.
		// "All rows" (`pageSize: -1`) has exactly one page, whatever the row count.
		const lastPage = paginationModel.pageSize > 0 ? Math.max(0, Math.ceil(groupedRows.length / paginationModel.pageSize) - 1) : 0;
		return paginationModel.page > lastPage ? { page: lastPage, pageSize: paginationModel.pageSize } : paginationModel;
	}, [groupedRows.length, paginationModel]);

	/** biome-ignore lint/correctness/useExhaustiveDependencies: `toggleRefreshExpanded` and `toggleRefreshPending` are
	 *  trigger-only. Unless `stableRowsOnToggle`, they force a fresh `rows` reference on every open, close and pending
	 *  change, which re-runs every visible cell's `renderCell`: the path every expansion grid shipped with. The chevron
	 *  and the detail cell (its Collapse `in`, the spinner) read the open and pending state from
	 *  `RowExpansionStateContext` either way, so under `stableRowsOnToggle` the rows change only with
	 *  `mountedDetailIds`. In cell mode a field swap changes neither `mountedDetailIds` nor the open set, so
	 *  `activeFieldByRow` is in the deps: it is what re-renders the detail cell with the newly active cell's content
	 *  (read via `activeFieldByRowRef`). It never changes in row mode, so it costs row mode nothing.
	 *  Rests on MUI internals (verified at `@mui/x-data-grid@9.12`): `DataGrid` is memoized, so a fresh
	 *  `rows` identity starts the render, while `GridRow`/`GridCell` are not, so `renderCell` re-runs.
	 *  Re-check both on a data-grid major upgrade.
	 */
	const rowsWithDetails = useMemo<GridRow<T>[]>(() => {
		if (!expandEnabled) return groupedRows;
		// Server-mode pagination: hand MUI only the current page's real rows, then interleave a detail
		// row after each expanded one. Slicing first keeps detail rows on their parent's page and out
		// of the footer count (the grid is told the real `rowCount` separately).
		const { page, pageSize } = effectivePaginationModel;
		// `pageSize: -1` is MUI's "All rows" sentinel. MUI honours it internally, but here WE do the
		// slicing, and `slice(0, 0 + -1)` silently drops the LAST row instead of keeping every one.
		const start = page * pageSize;
		const pageRows = pageSize > 0 ? groupedRows.slice(start, start + pageSize) : groupedRows;
		if (mountedDetailIds.size === 0) return pageRows;
		const out: GridRow<T>[] = [];
		for (const row of pageRows) {
			out.push(row);
			// Band rows are structural — they have no detail of their own and no caller row id.
			if (!isDataRow<T>(row)) continue;
			const id = getRowId(row);
			// Mounted = open or animating closed. `expandedIds` (in deps) drives a re-render on toggle so
			// the detail cell's Collapse `in` updates even when the mounted set itself is unchanged.
			if (mountedDetailIds.has(id) && resolveDetailContent(row as T) != null) {
				out.push({ __detailParentId: id, __detailParent: row });
			}
		}
		return out;
	}, [
		groupedRows,
		expandEnabled,
		mountedDetailIds,
		toggleRefreshExpanded,
		toggleRefreshPending,
		activeFieldByRow,
		getRowId,
		resolveDetailContent,
		effectivePaginationModel,
	]);

	/**
	 * The rows handed to MUI as its prop. MUI re-syncs a new `rows` prop in an effect, so the render that carries it
	 * runs every row and cell against the old state first: a whole extra pass. Under `stableRowsOnToggle` the prop
	 * changes only with the caller's rows, the page or the detail content (and then carries the details open at the
	 * time); a detail row mounting or unmounting reaches MUI through `apiRef.current.setRows`, which updates its state
	 * in one pass and leaves a later prop change in charge. Otherwise it is `rowsWithDetails` itself.
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: `rowsWithDetails` is read only when the caller's inputs change (or always, off the gate): a detail-only change goes through `setRows` below.
	const rowsProp = useMemo(
		() => rowsWithDetails,
		[stableRowsOnToggle ? null : rowsWithDetails, groupedRows, expandEnabled, getRowId, resolveDetailContent, effectivePaginationModel],
	);
	// What MUI holds: the prop as it last arrived (MUI re-syncs a new one itself), or what was last set through
	// `setRows`. Compared with that, not with the prop: closing the last detail returns the caller's own rows array,
	// the very one the prop still is, while MUI still holds the detail.
	const heldRowsRef = useRef(rowsProp);
	const lastRowsPropRef = useRef(rowsProp);
	useLayoutEffect(() => {
		if (lastRowsPropRef.current !== rowsProp) {
			lastRowsPropRef.current = rowsProp;
			heldRowsRef.current = rowsProp;
		}
		if (heldRowsRef.current === rowsWithDetails) return;
		heldRowsRef.current = rowsWithDetails;
		apiRef.current?.setRows(rowsWithDetails);
	}, [rowsProp, rowsWithDetails, apiRef]);

	/** Band rows carry a synthetic, namespaced id so they can't collide with a caller's row ids. */
	const groupAwareGetRowId = useCallback((row: GridRow<T>) => (isGroupRow(row) ? `${GROUP_ID_PREFIX}${row.__groupKey}` : getRowId(row as T)), [getRowId]);

	const effectiveGetRowId = useCallback(
		// Namespaced ids for the two synthetic row kinds, so MUI gets a unique key for each without
		// colliding with caller ids.
		(row: GridRow<T>) => (isDetailRow<T>(row) ? `${DETAIL_ID_PREFIX}${row.__detailParentId}` : groupAwareGetRowId(row)),
		[groupAwareGetRowId],
	);

	// Full-width detail content for a detail row's single (col-spanned) cell. `resolveDetailContent`
	// picks row-level vs the active cell's content.
	const renderDetail = useCallback(
		(parent: T, parentId: string | number) => (
			<RowDetailCell
				parentId={parentId}
				isolateKeys={isolateExpandedContentKeys}
				sx={expandedContentSx}
				testId={dataTestId ? `${dataTestId}-detail-${parentId}` : undefined}
			>
				{resolveDetailContent(parent)}
			</RowDetailCell>
		),
		[dataTestId, expandedContentSx, resolveDetailContent, isolateExpandedContentKeys],
	);

	const columnsWithExpand = useMemo<GridColDef<GridRow<T>>[]>(() => {
		// Two modes share the detail row but host it differently:
		//  • row mode  — prepend a chevron toggle column that spans full width on detail rows.
		//  • cell mode — no chevron; wrap the FIRST user column so it both renders its normal cell AND
		//                spans full width on detail rows. Every column is tagged with a cursor class on
		//                its expandable cells.
		// Open-state + active field are read via refs, so this memo doesn't depend on `expandedIds`/`activeFieldByRow`.
		const base = reorderableColumns as GridColDef<GridRow<T>>[];
		if (!expandEnabled) return base;

		if (cellExpandEnabled) {
			// Cell mode has no chevron, so it needs no leading gutter column: the FIRST data column both renders
			// its normal cell AND, on a detail row, col-spans the full width to host the drilldown panel. Every
			// column is tagged with a cursor class on its expandable cells.
			const totalCols = base.length;
			const tagExpandable = (col: GridColDef<GridRow<T>>): GridColDef<GridRow<T>> => {
				const callerClass = col.cellClassName;
				return {
					...col,
					cellClassName: (params) => {
						const inherited = typeof callerClass === "function" ? callerClass(params) : (callerClass ?? "");
						// `isDataRow`, not `!isDetailRow`: grouping composes with expansion, so the first column
						// also carries band rows — and a band is a `{ __groupKey }` object typed as `T`.
						const expandable = isDataRow<T>(params.row) && getCellExpandContent?.(params.row as T, col.field as keyof T) != null ? CELL_EXPANDABLE_CLASS : "";
						return [inherited, expandable].filter(Boolean).join(" ");
					},
				};
			};
			const tagged = base.map(tagExpandable);
			const [firstCol, ...restCols] = tagged;
			if (!firstCol) return tagged;
			const firstColRenderCell = firstCol.renderCell;
			const firstWithDetail: GridColDef<GridRow<T>> = {
				...firstCol,
				colSpan: (_value, row) => (isDetailRow<T>(row) ? totalCols : 1),
				renderCell: (params) =>
					isDetailRow<T>(params.row)
						? renderDetail(params.row.__detailParent, params.row.__detailParentId)
						: firstColRenderCell
							? firstColRenderCell(params)
							: ((params.formattedValue ?? params.value ?? null) as ReactNode),
			};
			return [firstWithDetail, ...restCols];
		}

		// Row mode: prepend a chevron toggle column. `sortable: false` makes the sortable-header
		// injection above skip it.
		const totalCols = base.length + 1; // +1 for the toggle column itself
		const toggleCol: GridColDef<GridRow<T>> = {
			field: EXPAND_FIELD,
			headerName: "",
			width: EXPAND_FIELD_WIDTH,
			sortable: false,
			editable: false,
			disableColumnMenu: true,
			renderHeader: showCollapseAllHeader
				? () => (
						<ExpandAllToggleHeaderCell
							onClick={requestCollapseAll}
							label={t("system.dataGrid.collapseAll")}
							icon={<UnfoldLessIcon fontSize="small" />}
							testId={dataTestId ? `${dataTestId}-collapse-all` : undefined}
						/>
					)
				: showExpandAll && !hasExpandedRows
					? () => (
							<ExpandAllToggleHeaderCell
								onClick={expandAll}
								label={t("system.dataGrid.expandAll")}
								icon={<UnfoldMoreIcon fontSize="small" />}
								testId={dataTestId ? `${dataTestId}-expand-all` : undefined}
							/>
						)
					: undefined,
			colSpan: (_value, row) => (isDetailRow<T>(row) ? totalCols : 1),
			renderCell: (params) => {
				if (isDetailRow<T>(params.row)) return renderDetail(params.row.__detailParent, params.row.__detailParentId);
				const row = params.row as T;
				const hasContent = getRowExpandContent?.(row) != null;
				const id = getRowId(row);
				// `isRowExpandable` decides visibility when given; without it the rule is the original one
				// (content decides), so every existing grid draws exactly the chevrons it drew before.
				// An OPEN row keeps its chevron either way: the caller's predicate can flip while a row is
				// open (a refetch says it is no longer expandable) while its detail is still mounted, and
				// hiding the toggle then would strand the detail with no way to close it.
				if (!(isRowExpandable ? isRowExpandable(row) || expandedIdsRef.current.has(id) : hasContent)) return null; // not expandable → empty cell
				// A lazy row: expandable, but opening it costs a fetch. Say so — a bare chevron is a weaker
				// promise than the button this replaced, and this label is the mitigation. A caller-supplied
				// per-row string wins over both defaults (e.g. "Try again" on a row whose last load failed).
				const isLazy = !hasContent && onRowExpandRequest != null;
				const loadingLabel = t("system.dataGrid.expandRowLoading");
				// Called ONCE per render (a caller's implementation may not be cheap), and `|| undefined`
				// rather than `??` so a caller returning "" is treated as "nothing to say" instead of
				// silently leaving the button with an empty accessible name.
				const callerLabel = getRowExpandTooltip?.(row) || undefined;
				const closedLabel = callerLabel ?? (isLazy ? t("system.dataGrid.loadAndExpandRow") : t("system.dataGrid.expandRow"));
				// Tooltip only where the chevron doesn't explain itself: mid-fetch, a lazy row, or a row the
				// caller has something to say about. A plain closed/open chevron keeps its bare look.
				const closedTooltip = callerLabel ?? (isLazy ? closedLabel : undefined);
				// The caller's per-row name for each state: the chevron reads which one it is in, so it can turn
				// without its cell re-rendering. `|| undefined`, as above, so "" means none.
				const callerExpandAriaLabel = getRowExpandAriaLabel?.(row, false) || undefined;
				const callerCollapseAriaLabel = getRowExpandAriaLabel?.(row, true) || undefined;
				return (
					<ExpandToggleCell
						rowId={id}
						onToggle={handleRowExpandToggle}
						// DEFAULT: the grid's wording ("Expand row" / "Collapse row", or the lazy variants) when the caller names nothing.
						expandLabel={callerExpandAriaLabel ?? closedLabel}
						collapseLabel={callerCollapseAriaLabel ?? t("system.dataGrid.collapseRow")}
						loadingLabel={loadingLabel}
						closedTooltip={closedTooltip}
						// True for any grid whose chevron tooltip can appear and disappear — i.e. one using either
						// lazy prop. Keeps the wrapper (and therefore the button's DOM node, and focus) stable
						// across pending → open; see `ExpandToggleCell`'s own note.
						keepTooltipMounted={onRowExpandRequest != null || getRowExpandTooltip != null}
						indicator={rowExpandIndicator}
						testId={dataTestId ? `${dataTestId}-expand-toggle-${id}` : undefined}
						loadingTestId={dataTestId ? `${dataTestId}-expand-loading-${id}` : undefined}
					/>
				);
			},
		};
		return [toggleCol, ...base];
	}, [
		expandEnabled,
		cellExpandEnabled,
		reorderableColumns,
		getRowExpandContent,
		isRowExpandable,
		onRowExpandRequest,
		getRowExpandTooltip,
		getRowExpandAriaLabel,
		rowExpandIndicator,
		getCellExpandContent,
		getRowId,
		handleRowExpandToggle,
		t,
		dataTestId,
		showCollapseAllHeader,
		requestCollapseAll,
		hasExpandedRows,
		showExpandAll,
		expandAll,
		renderDetail,
	]);

	/**
	 * The chevron + the caller's label node for one band, hosted by the band row's col-spanned cell. The group comes
	 * in with the band row, so this doesn't depend on the groups: a dependency would give the banded first column a
	 * new `renderCell` on every rows change (a filter keystroke, a fold), and MUI re-processes the whole column set.
	 */
	const renderGroupBand = useCallback(
		(group: RowGroup<T>, tabIndex: -1 | 0): ReactNode => {
			if (!rowGrouping) return null;
			const { key } = group;
			// The chevron is withheld while a search is active: folding is suppressed then (so a band can
			// never hide its own match), and a control that visibly does nothing is worse than no control.
			// An empty band (a persistent group with no rows) has nothing to fold either.
			const empty = group.rows.length === 0;
			const collapsible = (rowGrouping.collapsible ?? true) && !searchActive && !empty;
			return (
				<GroupHeaderCell
					collapsed={group.collapsed}
					collapsible={collapsible}
					empty={empty}
					toggleTarget={groupToggleTarget}
					label={rowGrouping.renderGroupLabel(group, { tabIndex })}
					ariaLabel={rowGrouping.getGroupAriaLabel?.(group) ?? t(group.collapsed ? "system.dataGrid.expandGroup" : "system.dataGrid.collapseGroup")}
					onToggle={() => toggleGroup(key)}
					testId={dataTestId ? `${dataTestId}-group-${key}` : undefined}
					tabIndex={tabIndex}
					alignToExpandColumn={rowExpandEnabled}
				/>
			);
		},
		[rowGrouping, searchActive, t, toggleGroup, dataTestId, groupToggleTarget, rowExpandEnabled],
	);

	/**
	 * Wraps the FIRST column of whatever the grid ends up rendering — the data columns, or the
	 * expand-toggle column when expansion is on — so a band row col-spans the full width and hosts the
	 * band instead of that column's cell. Wrapping the FINAL array is what makes grouping composable:
	 * a first column that already carries its own `colSpan`/`renderCell` keeps both for every real row.
	 */
	const applyGroupBands = useCallback(
		(cols: GridColDef<GridRow<T>>[]): GridColDef<GridRow<T>>[] => {
			if (!groupingEnabled) return cols;
			const [first, ...rest] = cols;
			if (!first) return cols;
			const totalCols = cols.length;
			const innerColSpan = first.colSpan;
			const innerRenderCell = first.renderCell;
			const banded: GridColDef<GridRow<T>> = {
				...first,
				colSpan: (value, row, column, gridApi) =>
					isGroupRow(row) ? totalCols : typeof innerColSpan === "function" ? innerColSpan(value, row, column, gridApi) : (innerColSpan ?? 1),
				renderCell: (params) =>
					isGroupRow<T>(params.row)
						? renderGroupBand(params.row.__group, params.tabIndex)
						: innerRenderCell
							? innerRenderCell(params)
							: ((params.formattedValue ?? params.value ?? null) as ReactNode),
			};
			return [banded, ...rest];
		},
		[groupingEnabled, renderGroupBand],
	);

	// Banding rebuilds the column array and clones its first column, so both call sites are memoized:
	// unmemoized they re-ran on EVERY render (a keystroke in the search box, a mutation settling in a
	// caller), handing MUI a fresh `columns` identity each time and forcing a full column re-evaluation.
	const bandedExpandColumns = useMemo(() => applyGroupBands(columnsWithExpand), [applyGroupBands, columnsWithExpand]);
	const bandedDefaultColumns = useMemo(
		() => (groupingEnabled ? applyGroupBands(reorderableColumns as unknown as GridColDef<GridRow<T>>[]) : reorderableColumns) as GridColDef<T>[],
		[groupingEnabled, applyGroupBands, reorderableColumns],
	);

	/**
	 * Grouping, default (non-expansion) branch: `dataGridProps` is spread wholesale onto the grid, so
	 * every caller callback that reads a row would otherwise be handed the grid's own band rows —
	 * `{ __groupKey }` objects typed as `T` with every real field `undefined`. The expansion branch
	 * guards each callback individually; this rebuilds the same guards as an overlay applied AFTER the
	 * spread. `processRowUpdate` / `isCellEditable` arrive as their own props and are guarded at the
	 * call site. `getRowClassName` / `getRowHeight` are not in this overlay because neither needs it:
	 * both branches install `gridGetRowClassName` / `effectiveGetRowHeight` AFTER the spread, and each
	 * answers a band row itself before deferring to the caller. Callbacks not listed here are documented
	 * as caller-guarded on `rowGrouping`.
	 */
	const groupGuardedCallerProps = useMemo<Partial<DataGridProps<T>>>(() => {
		if (!groupingEnabled || !dataGridProps) return {};
		const { onRowClick, onRowDoubleClick, onCellClick, onCellDoubleClick, isRowSelectable, getRowSpacing, getCellClassName } = dataGridProps;
		const guarded: Partial<DataGridProps<T>> = {};
		if (onRowClick)
			guarded.onRowClick = (params, event, details) => {
				if (isDataRow<T>(params.row)) onRowClick(params, event, details);
			};
		if (onRowDoubleClick)
			guarded.onRowDoubleClick = (params, event, details) => {
				if (isDataRow<T>(params.row)) onRowDoubleClick(params, event, details);
			};
		if (onCellClick)
			guarded.onCellClick = (params, event, details) => {
				if (isDataRow<T>(params.row)) onCellClick(params, event, details);
			};
		if (onCellDoubleClick)
			guarded.onCellDoubleClick = (params, event, details) => {
				if (isDataRow<T>(params.row)) onCellDoubleClick(params, event, details);
			};
		// A band is chrome, never a selectable record — `false` regardless of what the caller answers.
		if (isRowSelectable) guarded.isRowSelectable = (params) => isDataRow<T>(params.row) && isRowSelectable(params);
		if (getRowSpacing) guarded.getRowSpacing = (params) => (isGroupRow(params.model) ? {} : getRowSpacing(params));
		if (getCellClassName) guarded.getCellClassName = (params) => (isDataRow<T>(params.row) ? getCellClassName(params) : "");
		return guarded;
	}, [groupingEnabled, dataGridProps]);

	/** Band rows are not editable; a caller's `isCellEditable` never sees one. */
	const groupIsCellEditable = useCallback<NonNullable<DataGridProps<T>["isCellEditable"]>>(
		(params) => isDataRow<T>(params.row) && (isCellEditable?.(params) ?? true),
		[isCellEditable],
	);
	/** Defensive twin of the above: a band row edit is a no-op commit, never a caller row update. */
	const groupProcessRowUpdate = useCallback<NonNullable<DataGridProps<T>["processRowUpdate"]>>(
		(newRow, oldRow, params) => (isDataRow<T>(newRow) && processRowUpdate ? processRowUpdate(newRow, oldRow, params) : newRow),
		[processRowUpdate],
	);

	const groupHeaderHeight = rowGrouping?.headerHeight ?? DEFAULT_GROUP_HEADER_HEIGHT;
	// Through a ref: the grouping config is rebuilt on every fold toggle, and a new row-height function would make MUI
	// re-read every row's height. The caller resets the heights when the answers change (`getHeaderHeight`).
	const getGroupHeaderHeightRef = useLatestRef(rowGrouping?.getHeaderHeight);

	const effectiveGetRowHeight = useMemo(() => {
		// Detail rows: "auto" ONLY while animating open/closed, so the row height slides with the
		// <Collapse>. Once settled they switch to the caller's fixed px height — settled rows must never
		// be "auto", as that attaches a per-row ResizeObserver that re-measures on every scroll tick and
		// thrashes the grid into a render loop. Real rows defer to a caller's getRowHeight (if any).
		// The transitioning set is read through its ref, not as a dependency: a new function identity makes
		// MUI re-read EVERY row's height, and the set changes twice per toggle. The `resetRowHeights` effect
		// on `transitioningIds` (above) is what re-reads heights when a row starts or ends its transition.
		const callerFn = dataGridProps?.getRowHeight as DataGridProps<GridRow<T>>["getRowHeight"] | undefined;
		if (!expandEnabled && !groupingEnabled) return callerFn;
		const fn: DataGridProps<GridRow<T>>["getRowHeight"] = (params) => {
			if (groupingEnabled && isGroupRow<T>(params.model)) {
				const getHeaderHeight = getGroupHeaderHeightRef.current;
				return getHeaderHeight === undefined ? groupHeaderHeight : getHeaderHeight(params.model.__group);
			}
			if (isDetailRow<T>(params.model)) {
				return transitioningIdsRef.current.has(params.model.__detailParentId) ? "auto" : resolveDetailHeight(params.model.__detailParent);
			}
			return callerFn?.(params) ?? null;
		};
		return fn;
	}, [expandEnabled, groupingEnabled, groupHeaderHeight, getGroupHeaderHeightRef, dataGridProps, resolveDetailHeight]);

	// Under `stableRowsOnToggle`: a detail animating at "auto" height is first laid out at MUI's estimate before it is
	// measured. MUI's default estimate is one standard row, which is neither where an opening detail starts (its
	// Collapse is shut) nor where a closing one does (fully open), so the rows under it would leave or mount for a
	// frame and come straight back. Estimated where its Collapse starts, they stay put. Other rows keep the caller's.
	const effectiveGetEstimatedRowHeight = useMemo(() => {
		const callerFn = dataGridProps?.getEstimatedRowHeight as DataGridProps<GridRow<T>>["getEstimatedRowHeight"] | undefined;
		const fn: DataGridProps<GridRow<T>>["getEstimatedRowHeight"] = (params) => {
			if (!isDetailRow<T>(params.model)) return callerFn?.(params) ?? null;
			return expandedIdsRef.current.has(params.model.__detailParentId) ? COLLAPSED_DETAIL_HEIGHT_PX : resolveDetailHeight(params.model.__detailParent);
		};
		return fn;
	}, [dataGridProps, resolveDetailHeight]);

	// `dataGridProps` carries caller callbacks typed on T; this is the same object, retyped for the
	// GridRow grid. It's a pure type assertion (no runtime work), so it stays referentially stable.
	const expandCallerProps = dataGridProps as unknown as Partial<DataGridProps<GridRow<T>>> | undefined;

	// All callbacks/sx handed to the expand-branch DataGrid are memoized so a toggle (which only
	// changes `expandedIds` → `rowsWithDetails`) doesn't hand MUI fresh prop identities and force it
	// to re-evaluate every row. Both synthetic row kinds are guarded out — `isDataRow` rather than
	// `!isDetailRow`, because grouping and expansion compose, so this branch can also carry bands.
	// `?? true` matches MUI's own answer for an absent prop, so this can be installed unconditionally —
	// which is what keeps a synthetic row out of edit mode even when the caller asks nothing.
	const expandIsCellEditable = useCallback(
		(params: GridCellParams<GridRow<T>>) =>
			isDataRow<T>(params.row) && ((isCellEditable as unknown as ((p: GridCellParams<GridRow<T>>) => boolean) | undefined)?.(params) ?? true),
		[isCellEditable],
	);
	const expandOnRowClick = useCallback<NonNullable<DataGridProps<GridRow<T>>["onRowClick"]>>(
		(params, event, details) => {
			if (isDataRow<T>(params.row)) expandCallerProps?.onRowClick?.(params, event, details);
		},
		[expandCallerProps],
	);
	// Shared by both branches: the two synthetic row kinds get their own class (and never reach a
	// caller's classifier, which is written for real rows only).
	const gridGetRowClassName = useCallback<NonNullable<DataGridProps<GridRow<T>>["getRowClassName"]>>(
		(params) => {
			if (isGroupRow(params.row)) return params.row.__groupEmpty ? `${GROUP_ROW_CLASS} ${GROUP_ROW_EMPTY_CLASS}` : GROUP_ROW_CLASS;
			if (isDetailRow<T>(params.row)) return DETAIL_ROW_CLASS;
			return expandCallerProps?.getRowClassName?.(params) ?? "";
		},
		[expandCallerProps],
	);
	const expandOnCellClick = useCallback<NonNullable<DataGridProps<GridRow<T>>["onCellClick"]>>(
		(params, event, details) => {
			if (!isDataRow<T>(params.row)) return; // clicks on a detail area or a band never toggle
			// MUI types `field` as `string`; our API narrows it to `keyof T` (data columns).
			const field = params.field as keyof T;
			if (cellExpandEnabled && getCellExpandContent?.(params.row as T, field) != null) {
				event.stopPropagation();
				handleCellExpand(getRowId(params.row as T), field);
				return;
			}
			expandCallerProps?.onCellClick?.(params, event, details);
		},
		[cellExpandEnabled, getCellExpandContent, handleCellExpand, getRowId, expandCallerProps],
	);
	/**
	 * Keyboard parity for cell expansion. An expandable cell is opened by CLICKING it — which left the
	 * panel unreachable without a mouse, since the cell carries no button of its own to tab to (the
	 * affordance drawn inside it is decorative). MUI puts keyboard focus on the `gridcell` itself, so
	 * Enter/Space there is the one key event that means "activate this cell"; nothing else in the grid
	 * claims it for a non-editable cell. Row-expansion grids are untouched — their chevron is already a
	 * real focusable button.
	 */
	const expandOnCellKeyDown = useCallback<NonNullable<DataGridProps<GridRow<T>>["onCellKeyDown"]>>(
		(params, event, details) => {
			if (cellExpandEnabled && (event.key === "Enter" || event.key === " ") && isDataRow<T>(params.row)) {
				const field = params.field as keyof T;
				if (getCellExpandContent?.(params.row as T, field) != null) {
					// Space would scroll the grid and Enter would bubble to an ancestor form/row handler.
					event.preventDefault();
					event.stopPropagation();
					handleCellExpand(getRowId(params.row as T), field);
					return;
				}
			}
			expandCallerProps?.onCellKeyDown?.(params, event, details);
		},
		[cellExpandEnabled, getCellExpandContent, handleCellExpand, getRowId, expandCallerProps],
	);
	const expandOnPaginationModelChange = useCallback<NonNullable<DataGridProps<GridRow<T>>["onPaginationModelChange"]>>(
		// Controlled pagination: persist our model so detail rows are sliced into the right page.
		// Expansion is intentionally NOT reset on page change — details always stay on their parent's
		// page, so expanded rows can persist across navigation. Forwards to a caller-supplied handler.
		(model, details) => {
			setPaginationModel(model);
			// Snap any in-flight expand/collapse animations: cancel timers, drop transitions, and prune the
			// mounted set back to the open set (closing rows just vanish with the page they were on).
			for (const timer of animationTimers.current.values()) clearTimeout(timer);
			animationTimers.current.clear();
			setTransitioningIds((prev) => (prev.size === 0 ? prev : new Set()));
			setMountedDetailIds((prev) => (prev.size === expandedIdsRef.current.size ? prev : new Set(expandedIdsRef.current)));
			// Cell mode: prune active-field entries for rows whose detail just vanished with the page.
			setActiveFieldByRow((prev) => {
				if (prev.size === 0) return prev;
				const next = new Map<string | number, keyof T>();
				for (const [id, field] of prev) if (expandedIdsRef.current.has(id)) next.set(id, field);
				return next.size === prev.size ? prev : next;
			});
			expandCallerProps?.onPaginationModelChange?.(model, details);
		},
		[expandCallerProps],
	);

	// ── Checkbox row selection under expansion (`dataGridProps.checkboxSelection` pass-through) ──
	// Expansion interleaves synthetic detail rows and hosts the detail content in the row's FIRST
	// cell via colSpan. With checkboxSelection on, MUI prepends its `__check__` column, so the
	// detail must move there (spanning check + expand-toggle + data columns) and detail rows must
	// never be selectable / show a checkbox.
	const checkboxSelectionEnabled = !!dataGridProps?.checkboxSelection;
	const expandCheckboxColDef = useMemo<DataGridProps<GridRow<T>>["checkboxColDef"]>(() => {
		if (!expandEnabled || !checkboxSelectionEnabled) return expandCallerProps?.checkboxColDef;
		const totalCols = reorderableColumns.length + 2; // __check__ + leading expand column + data columns
		return {
			...expandCallerProps?.checkboxColDef,
			// Detail rows: the checkbox column is now the row's first cell, so IT hosts the full-width
			// detail content (the expand column's own detail branch is covered by this span).
			colSpan: (_value, row) => (isDetailRow<T>(row) ? totalCols : 1),
			renderCell: (params) =>
				isDetailRow<T>(params.row)
					? renderDetail(params.row.__detailParent, params.row.__detailParentId)
					: GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params),
		};
	}, [expandEnabled, checkboxSelectionEnabled, expandCallerProps, reorderableColumns.length, renderDetail]);
	// Detail rows and bands are structural, never selectable; real rows defer to a caller predicate.
	const expandIsRowSelectable = useCallback(
		(params: GridRowParams<GridRow<T>>) =>
			isDataRow<T>(params.row) && ((expandCallerProps?.isRowSelectable as ((p: GridRowParams<GridRow<T>>) => boolean) | undefined)?.(params) ?? true),
		[expandCallerProps],
	);
	// Expansion paginates in server mode internally, so MUI can't enumerate the full row set and
	// reports header select-all as an `exclude` model. Normalize it to a concrete `include` set over
	// the currently displayed (searched + sorted) real rows, so callers never see exclude semantics.
	const displayRowsRef = useRef(displayRows);
	displayRowsRef.current = displayRows;
	const expandOnRowSelectionModelChange = useCallback<NonNullable<DataGridProps<GridRow<T>>["onRowSelectionModelChange"]>>(
		(model, details) => {
			const callerOnChange = expandCallerProps?.onRowSelectionModelChange;
			if (!callerOnChange) return;
			if (model.type === "exclude") {
				const ids = new Set<GridRowId>();
				for (const row of displayRowsRef.current) {
					const id = getRowId(row);
					if (!model.ids.has(id)) ids.add(id);
				}
				callerOnChange({ type: "include", ids }, details);
				return;
			}
			callerOnChange(model, details);
		},
		[expandCallerProps, getRowId],
	);
	// Drop-indicator styles, layered on only when reorder is enabled (stable module-level reference).
	const reorderMarkerSx = enableColumnReorder ? columnReorderMarkerSx : undefined;
	const baseGridSx = useMemo(() => mergeSx(mergeSx(gridBaseProps.sx, effectiveSx), reorderMarkerSx), [effectiveSx, reorderMarkerSx]);
	// In contained mode, keep the loading skeleton at the same content size + chrome so it doesn't jump on first data.
	const skeletonGridSx = useMemo(() => (containedHeightSx ? mergeSx(baseGridSx, containedHeightSx) : baseGridSx), [baseGridSx, containedHeightSx]);
	const showEmptyOverlay = emptyState != null && !loading && !error && displayRows.length === 0;
	// With a custom emptyState: drop the brand overlay wash, and (when empty) floor the height so it isn't clipped.
	// Shared by both the default and expandable branches so an expandable grid gets the same clean empty state
	// instead of MUI's default "No rows" band.
	const emptyOverlaySx = useMemo<DataGridProps<T>["sx"] | null>(() => {
		if (emptyState == null) return null;
		return showEmptyOverlay
			? { "& .MuiDataGrid-overlay": { backgroundColor: "transparent" }, minHeight: EMPTY_OVERLAY_MIN_HEIGHT + EMPTY_GRID_CHROME_ALLOWANCE }
			: { "& .MuiDataGrid-overlay": { backgroundColor: "transparent" } };
	}, [emptyState, showEmptyOverlay]);
	const defaultBranchSx = useMemo(() => (emptyOverlaySx ? mergeSx(baseGridSx, emptyOverlaySx) : baseGridSx), [baseGridSx, emptyOverlaySx]);
	// Sort items for the per-column menu, only when this grid sorts at all. A caller that already
	// supplies its own `columnMenu` slot keeps it.
	const sortColumnMenu = useMemo(
		() =>
			sortEnabled
				? buildSystemGridColumnMenu<T>({
						sortState: sortHook.sortState,
						setSortState: sortHook.setSortState,
						optionByField,
						headerClickMode,
						retainEntry: retainInvisibleSortEntry,
						canFilterByColumn,
						onFilterByColumn,
						filterByHint,
					})
				: undefined,
		[
			sortEnabled,
			sortHook.sortState,
			sortHook.setSortState,
			optionByField,
			headerClickMode,
			retainInvisibleSortEntry,
			canFilterByColumn,
			onFilterByColumn,
			filterByHint,
		],
	);
	// "Sorted by Role ↑, then Name ↑" — the labels come from `sortingOptions`, so the wording matches
	// the sort popup rather than restating raw field names.
	const sortSummary = useMemo(() => {
		if (!showSortSummary || sortHook.sortState.length === 0) return null;
		const parts = sortHook.sortState.map((entry) => {
			const label = entry.label ?? findMatchingSortOption(entry.sortColumns, sortingOptions)?.label ?? String(entry.sortColumns[0]);
			return `${label} ${entry.sortOrder === "asc" ? "↑" : "↓"}`;
		});
		// "Role ↑, then Name ↑" — the joiner is translated because word order differs by language.
		const joined = parts.length > 1 ? `${parts[0]}, ${t("system.dataGrid.sortSummary.then")} ${parts.slice(1).join(", ")}` : parts[0];
		return t("system.dataGrid.sortSummary", { keys: joined });
	}, [showSortSummary, sortHook.sortState, sortingOptions, t]);

	/**
	 * Footer carrying the sort summary beside the pagination.
	 *
	 * The summary started life as its own line under the grid, which gave the table two stacked strips
	 * of chrome and left the footer's own row half empty. MUI's footer only ever holds pagination on
	 * the right, so the summary goes in the space already there.
	 */
	const SortSummaryFooter = useMemo(() => {
		if (!sortSummary) return undefined;
		return function Footer() {
			return (
				<GridFooterContainer>
					<Box
						sx={{ pl: 1.5, fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
						data-testid={dataTestId ? `${dataTestId}-sort-summary` : undefined}
					>
						{sortSummary}
					</Box>
					<GridPagination />
				</GridFooterContainer>
			);
		};
	}, [sortSummary, dataTestId]);

	/**
	 * Slot layering, resolved once for BOTH render branches.
	 *
	 * The expandable branch used to build its slots straight from the caller's, so the column-menu sort
	 * items and the sort summary silently never rendered on any grid using row/cell expansion. Injected
	 * slots only ever fill what the caller left empty; `emptyState` is the exception, since passing it is
	 * itself the request for our overlay. `slots` is not generic over the row type, so one object serves
	 * both the `T` grid and the `GridRow<T>` one.
	 */
	const mergedSlots = useMemo<DataGridProps<T>["slots"]>(() => {
		const callerGridSlots = dataGridProps?.slots;
		const withColumnMenu = sortColumnMenu && !callerGridSlots?.columnMenu ? { ...callerGridSlots, columnMenu: sortColumnMenu } : callerGridSlots;
		const withFooter = SortSummaryFooter && !callerGridSlots?.footer ? { ...withColumnMenu, footer: SortSummaryFooter } : withColumnMenu;
		return emptyState != null ? { ...withFooter, noRowsOverlay: NoRowsOverlay } : withFooter;
	}, [dataGridProps?.slots, sortColumnMenu, SortSummaryFooter, emptyState]);
	const noRowsSlotProps: NonNullable<DataGridProps<T>["slotProps"]>["noRowsOverlay"] = emptyState != null ? { emptyContent: emptyState } : undefined;
	// Memoized: `DataGrid` is memoized, so a fresh `slotProps` object makes every render of this component re-render
	// the whole MUI grid, even when nothing it shows changed (a controlled `expandedRowIds` change renders here once
	// before the reconcile does the real work).
	const callerSlotProps = expandCallerProps?.slotProps;
	const expandSlotProps = useMemo(
		() => (emptyState != null ? { ...callerSlotProps, noRowsOverlay: { emptyContent: emptyState } } : callerSlotProps),
		[emptyState, callerSlotProps],
	);
	const expandedGridSx = useMemo(() => {
		// Detail-cell padding reset, plus (cell mode only) the cursor-pointer affordance for expandable cells.
		const withReset = mergeSx(gridBaseProps.sx, detailRowCellResetSx);
		const withCursor = cellExpandEnabled ? mergeSx(withReset, cellExpandableCursorSx) : withReset;
		const merged = mergeSx(mergeSx(withCursor, effectiveSx), reorderMarkerSx);
		return emptyOverlaySx ? mergeSx(merged, emptyOverlaySx) : merged;
	}, [effectiveSx, cellExpandEnabled, reorderMarkerSx, emptyOverlaySx]);

	// Reset affordance: only once the user has actually customized the order, and only over the live grid.
	const showColumnReset = enableColumnReorder && (columnOrderCustomized || columnResetVisibility === "always") && !loading && !error;
	const showToolbar =
		searchEnabled ||
		(sortEnabled && !hideSortButton) ||
		exportEnabled ||
		columnPickerEnabled ||
		!!leadingToolbarNodes ||
		!!trailingToolbarNodes ||
		!!toolbarFooter ||
		showColumnReset;
	const skeletonWidths = useSkeletonWidths(skeletonRowCount, effectiveColumns.length || 1);

	// The built-in controls, sort through reset — built once; `keepToolbarControlsTogether` only decides the wrapper.
	const toolbarControls = (
		<>
			{sortEnabled && !hideSortButton && (
				<Box data-testid={dataTestId ? `${dataTestId}-sort-button` : undefined} sx={{ pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.5 : 1 }}>
					{/* The wrapping Box carries the testid (it also owns the loading state), so the button
					    itself takes none — two elements answering the same id is a strict-mode violation. */}
					<SystemSortingButtonWithPopup<T>
						sortingOptions={sortingOptions}
						localStorageTableKey={localStorageKey}
						initialState={sortHook.sortState}
						setSortState={sortHook.setSortState}
						defaultSortState={sortHook.defaultSortState}
						overridePopoverProps={{ containerStyle: { zIndex: TOOLBAR_POPUP_Z_INDEX } }}
						iconOnly={sortOnlyShowIcon}
					/>
				</Box>
			)}
			{exportEnabled && (
				<GridExportButton
					onExport={handleExport}
					// DEFAULT: a grid that doesn't restrict formats offers every one, as it did before `formats` existed.
					formats={exportOptions?.formats ?? EXPORT_FORMATS}
					disabled={loading || error || displayRows.length === 0}
					dataTestId={dataTestId ? `${dataTestId}-export` : undefined}
				/>
			)}
			{columnPickerEnabled && (
				<ColumnPickerPopover<T>
					// Display order, so the list reads like the header and a drag in it moves what it shows.
					columns={orderedColumns}
					syntheticColumns={columnPicker?.syntheticColumns}
					visibilityModel={columnVisibilityModel}
					onChange={handlePickerVisibilityChange}
					onReset={handlePickerVisibilityReset}
					groupLabels={columnPicker?.groupLabels}
					groupSelectAll={columnPicker?.groupSelectAll}
					trendColumnMatcher={columnPicker?.trendColumnMatcher}
					zIndex={columnPicker?.zIndex ?? TOOLBAR_POPUP_Z_INDEX}
					disabled={loading}
					testIdBase={dataTestId ? `${dataTestId}-column-picker` : undefined}
					onReorder={enableColumnReorder ? handlePickerReorder : undefined}
					onlyShowIcon={columnPicker?.onlyShowIcon}
				/>
			)}
			{trailingToolbarNodes}
			{showColumnReset && (
				<InHouseTooltip title={t("system.dataGrid.resetColumnOrder")}>
					{/* A span, so the tooltip still answers while the button is disabled (a disabled button gets no hover). */}
					<Box component="span" sx={{ display: "inline-flex" }}>
						<IconButton
							size="small"
							// Only `"always"` can show it uncustomized: there is nothing to reset yet.
							disabled={!columnOrderCustomized}
							onClick={() => setResetConfirmOpen(true)}
							aria-label={t("system.dataGrid.resetColumnOrder")}
							data-testid={dataTestId ? `${dataTestId}-reset-column-order` : undefined}
						>
							<RestartAltIcon fontSize="small" />
						</IconButton>
					</Box>
				</InHouseTooltip>
			)}
		</>
	);

	const toolbar = showToolbar ? (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				flexWrap: "wrap",
				// MUI DataGrid sets `.MuiDataGrid-cell`'s `line-height` equal to the ROW's own height (its
				// built-in trick for vertically centering plain, unwrapped text) — harmless for block-level
				// content, but it inherits down into WHATEVER renders inside a cell, including a caller's
				// `getRowExpandContent` detail area. A tall row (e.g. a nested SystemDataGrid's own
				// expandable detail row) inherits a correspondingly huge line-height; for an inline-level
				// child here (an IconButton is `display:inline-flex`) that inflates the invisible inline
				// "strut" to match it, ballooning this toolbar to the row's full height instead of its own
				// content height (2026-09-03 fix — confirmed via a nested SystemDataGrid whose toolbar
				// rendered as just a giant sort icon, pushing its own grid rows out of view below the fold).
				lineHeight: "normal",
				// Flush mode: the divider replaces the gap, so the toolbar and the grid read as one surface.
				...(flushToolbar ? { px: 1.5, py: 1, borderBottom: `1px solid ${NEUTRAL[200]}` } : {}),
				...toolbarSxOverride,
			}}
			data-testid={dataTestId ? `${dataTestId}-toolbar` : undefined}
		>
			{leadingToolbarNodes}
			{searchEnabled && (
				<SystemSearchField
					value={search}
					onChange={(value) => {
						if (controlledSearchValue === undefined) setInternalSearch(value);
						onSearchChange?.(value);
					}}
					placeholder={searchPlaceholder}
					name="grid-search"
					width={searchWidth}
					disabled={loading}
					dataTestId={dataTestId ? `${dataTestId}-search` : undefined}
				/>
			)}
			{/* Grouped (opt-in), the controls are ONE flex item: a toolbar that wraps moves them as a unit, centred on
			    their own line, instead of splitting Sort from Columns. */}
			{keepToolbarControlsTogether ? (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }} data-testid={dataTestId ? `${dataTestId}-toolbar-controls` : undefined}>
					{toolbarControls}
				</Box>
			) : (
				toolbarControls
			)}
			{/* Its own line: a full-width flex item wraps below the rest. Empty, it takes no line (and no gap). */}
			<Box sx={{ flexBasis: "100%", minWidth: 0, "&:empty": { display: "none" } }}>{toolbarFooter}</Box>
		</Box>
	) : null;

	// Skeleton rows + columns: keep the real headers (so the table looks like itself) but
	// replace each cell with a random-width Skeleton. Stable for the lifetime of the mount.
	const skeletonRows = useMemo<SkeletonRow[]>(() => Array.from({ length: skeletonRowCount }, (_, i) => ({ __skeletonId: i })), [skeletonRowCount]);
	const skeletonColumns = useMemo<GridColDef<SkeletonRow>[]>(
		() =>
			effectiveColumns.map((col, cIdx) => ({
				...(col as unknown as GridColDef<SkeletonRow>),
				sortable: false,
				editable: false,
				// Skeleton rows contain only `__skeletonId`. Consumer accessors expect the real row
				// shape and MUI evaluates them before `renderCell`, so explicitly remove them here.
				valueGetter: undefined,
				valueFormatter: undefined,
				renderCell: (params) => {
					const widthPct = skeletonWidths[params.row.__skeletonId]?.[cIdx] ?? 60;
					return (
						<Box
							sx={{
								width: "100%",
								height: "100%",
								display: "flex",
								alignItems: "center",
							}}
						>
							<Skeleton variant="text" sx={{ width: `${widthPct}%`, height: "60%" }} data-testid={dataTestId ? `${dataTestId}-skeleton-cell` : undefined} />
						</Box>
					);
				},
			})),
		[effectiveColumns, skeletonWidths, dataTestId],
	);

	// Non-expandable branch under grouping: band rows are structurally `GridRow<T>` while the branch is
	// typed on T. This retype is the same pure type assertion the expansion branch makes for
	// `dataGridProps` — no runtime work; `groupGuardedCallerProps` is what keeps band rows out of the
	// caller's callbacks (the cast alone would hide them from the compiler, not from the caller).
	const defaultBranchRows = (groupingEnabled ? (groupedRows as unknown as T[]) : displayRows) as T[];

	let body: ReactNode;
	if (error) {
		body = (
			<Box sx={{ p: 2 }} data-testid={dataTestId ? `${dataTestId}-error` : undefined}>
				<Typography color="error">{errorMessage ?? t("system.dataGrid.loadError")}</Typography>
			</Box>
		);
	} else if (loading) {
		body = (
			<Box
				sx={{ height: containedHeightSx ? "auto" : "100%", width: "100%", pointerEvents: "none" }}
				data-testid={dataTestId ? `${dataTestId}-skeleton` : undefined}
				role="status"
				aria-busy="true"
			>
				<DataGrid<SkeletonRow>
					{...gridBaseProps}
					rows={skeletonRows}
					columns={skeletonColumns}
					getRowId={(row) => row.__skeletonId}
					// Keep the group band in the skeleton so the header doesn't grow a row when data lands.
					columnGroupingModel={columnGroupingModel}
					hideFooter
					// Mirror the real grid's visibility. `skeletonColumns` is built from the FULL column set, so
					// without this a hidden column renders during load and then vanishes when data lands —
					// reading as "my column selection reset". Read-only: the picker is `disabled` while loading,
					// so there is no toggle to write back.
					{...(columnPickerEnabled && { columnVisibilityModel })}
					sx={skeletonGridSx}
				/>
			</Box>
		);
	} else if (expandEnabled) {
		// Expansion on: render over GridRow (real rows + interleaved detail rows). All props are
		// memoized above so toggling a row (which only changes `rows`) doesn't re-render the others.
		// `withColumnReorder` mounts the dnd-kit providers around the grid only when reorder is enabled.
		body = withColumnReorder(
			<RowExpansionStateContext.Provider value={rowExpansionState}>
				<DataGrid<GridRow<T>>
					{...gridBaseProps}
					{...expandCallerProps}
					// Grouping composes with expansion, so this branch can carry bands too. The same overlay the
					// default branch uses guards the callbacks that have no explicit wiring below
					// (`onRowDoubleClick`, `onCellDoubleClick`, `getRowSpacing`, `getCellClassName`); it is `{}`
					// when grouping is off, so the expansion-only callers are untouched.
					{...(groupGuardedCallerProps as Partial<DataGridProps<GridRow<T>>>)}
					// Native column picker owns visibility when enabled — placed after the caller spread so it wins,
					// and MUI's built-in column selector is suppressed so the toolbar picker is the single source.
					{...(columnPickerEnabled && {
						columnVisibilityModel,
						onColumnVisibilityModelChange: setColumnVisibilityModel,
						disableColumnSelector: true,
					})}
					// After the caller spread: persist resized widths (the handler forwards to any caller handler).
					{...(persistColumnWidths && { onColumnWidthChange: handleColumnWidthChange })}
					apiRef={apiRef}
					rows={rowsProp}
					columns={bandedExpandColumns}
					columnGroupingModel={columnGroupingModel}
					getRowId={effectiveGetRowId}
					getRowHeight={effectiveGetRowHeight}
					{...(stableRowsOnToggle && { getEstimatedRowHeight: effectiveGetEstimatedRowHeight })}
					processRowUpdate={(groupingEnabled && processRowUpdate ? groupProcessRowUpdate : processRowUpdate) as DataGridProps<GridRow<T>>["processRowUpdate"]}
					onProcessRowUpdateError={onProcessRowUpdateError}
					isCellEditable={expandIsCellEditable}
					onRowClick={expandCallerProps?.onRowClick ? expandOnRowClick : undefined}
					onCellClick={cellExpandEnabled || expandCallerProps?.onCellClick ? expandOnCellClick : undefined}
					onCellKeyDown={cellExpandEnabled || expandCallerProps?.onCellKeyDown ? expandOnCellKeyDown : undefined}
					getRowClassName={gridGetRowClassName}
					// Checkbox selection pass-through: detail rows are unselectable, the __check__ column hosts
					// the full-width detail cell, and exclude-model select-all is normalized for the caller.
					checkboxColDef={expandCheckboxColDef}
					isRowSelectable={checkboxSelectionEnabled || expandCallerProps?.isRowSelectable ? expandIsRowSelectable : undefined}
					onRowSelectionModelChange={expandCallerProps?.onRowSelectionModelChange ? expandOnRowSelectionModelChange : undefined}
					// DEFAULT: under the internal server-mode pagination, rows on other pages "don't exist" to
					// MUI and their selection would be silently pruned — keep them unless the caller opts out.
					keepNonExistentRowsSelected={expandCallerProps?.keepNonExistentRowsSelected ?? checkboxSelectionEnabled}
					slots={mergedSlots}
					slotProps={expandSlotProps}
					// Controlled, server-mode pagination so injected detail rows stay on their parent's page
					// and don't inflate the footer count (`rowCount` is the real row total). `initialState`
					// is cleared to avoid MUI's controlled-model-vs-initialState conflict warning.
					paginationMode="server"
					rowCount={groupedRows.length}
					paginationModel={effectivePaginationModel}
					onPaginationModelChange={expandOnPaginationModelChange}
					initialState={undefined}
					sx={expandedGridSx}
				/>
			</RowExpansionStateContext.Provider>,
		);
	} else {
		body = withColumnReorder(
			<DataGrid<T>
				{...gridBaseProps}
				{...dataGridProps}
				// After the caller spread so it wins: re-wraps the caller's own row/cell callbacks so the
				// grid's injected band rows never reach them. Empty ({}) unless grouping is on.
				{...groupGuardedCallerProps}
				// Native column picker owns visibility when enabled — placed after `dataGridProps` so it wins,
				// and MUI's built-in column selector is suppressed so the toolbar picker is the single source.
				{...(columnPickerEnabled && {
					columnVisibilityModel,
					onColumnVisibilityModelChange: setColumnVisibilityModel,
					disableColumnSelector: true,
				})}
				// After the caller spread: persist resized widths (the handler forwards to any caller handler).
				{...(persistColumnWidths && { onColumnWidthChange: handleColumnWidthChange })}
				apiRef={apiRef}
				rows={defaultBranchRows}
				columns={bandedDefaultColumns}
				columnGroupingModel={columnGroupingModel}
				getRowId={groupingEnabled ? (groupAwareGetRowId as (row: T) => string | number) : getRowId}
				{...(groupingEnabled && {
					getRowClassName: gridGetRowClassName as NonNullable<DataGridProps<T>["getRowClassName"]>,
					getRowHeight: effectiveGetRowHeight as DataGridProps<T>["getRowHeight"],
				})}
				processRowUpdate={groupingEnabled && processRowUpdate ? groupProcessRowUpdate : processRowUpdate}
				onProcessRowUpdateError={onProcessRowUpdateError}
				isCellEditable={groupingEnabled ? groupIsCellEditable : isCellEditable}
				slots={mergedSlots}
				slotProps={{
					...anchoredPanelSlotProps,
					...dataGridProps?.slotProps,
					basePopper: { ...anchoredPanelSlotProps.basePopper, ...dataGridProps?.slotProps?.basePopper },
					panel: { ...anchoredPanelSlotProps.panel, ...dataGridProps?.slotProps?.panel },
					...(noRowsSlotProps && { noRowsOverlay: noRowsSlotProps }),
				}}
				sx={defaultBranchSx}
			/>,
		);
	}

	// MUI DataGrid swallows arbitrary `data-testid` on its root, so we hang the grid testid
	// on the wrapping Box. The actual `.MuiDataGrid-root` is a descendant of this Box —
	// tests that need the DataGrid root specifically should `.locator(".MuiDataGrid-root")`.
	// In contained-height mode the wrappers drop their `height: 100%` / `flex: 1` so the grid's own height drives size.
	return (
		<Box
			// `minHeight: 0` (fill mode only) lets the root shrink below its content when it's a flex item — without
			// it, a flex parent's default `min-height: auto` keeps the root at full content height and it overflows
			// (e.g. a tall all-rows grid inside a bounded dialog box), instead of filling the parent and scrolling.
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: flushToolbar ? 0 : 1,
				height: containedHeightSx ? "auto" : "100%",
				...(containedHeightSx ? {} : { minHeight: 0 }),
				width: "100%",
			}}
			data-testid={dataTestId ? `${dataTestId}-root` : undefined}
		>
			{toolbar}
			<Box
				sx={containedHeightSx ? { minHeight: 0 } : { flex: 1, minHeight: 0 }}
				data-testid={dataTestId && !error && !loading ? `${dataTestId}-grid` : undefined}
			>
				{body}
			</Box>
			{/* Lazy expansion is the one flow whose success is otherwise announced by nothing: the spinner
			    stops, the row opens below the focused chevron, and a screen-reader user is told neither. The
			    region only exists for grids using `onRowExpandRequest`, is polite (never interrupts), and is
			    emptied when the next request starts — so two consecutive loads each produce a real text
			    change and therefore a real announcement. */}
			{onRowExpandRequest != null && (
				<Box role="status" aria-live="polite" sx={visuallyHidden} data-testid={dataTestId ? `${dataTestId}-expand-status` : undefined}>
					{expandStatusMessage}
				</Box>
			)}
			<SystemDialog
				open={resetConfirmOpen}
				onClose={() => setResetConfirmOpen(false)}
				title={t("system.dataGrid.resetColumnOrder.confirmTitle")}
				body={t("system.dataGrid.resetColumnOrder.confirmBody")}
				confirm={t("system.dataGrid.resetColumnOrder.confirmButton")}
				onConfirm={handleResetConfirmed}
				dataTestId={dataTestId ? `${dataTestId}-reset-column-order-dialog` : undefined}
			/>
		</Box>
	);
}
